// src/components/Atelier4/OperationalScenariosAI.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Target,
  Brain,
  Zap,
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  Scale,
  MinusCircle,
  Package,
  Eye,
  Shield,
  ArrowRight,
  Save,
  RefreshCw,
  Plus,
  X,
  Trash2,
  BrainCircuit,
  ChevronDown,
  HelpCircle,
  Info,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { motion, AnimatePresence } from "framer-motion";
import { ReactFlowProvider } from "reactflow"; // Still needed for PhaseVisualization
import { api } from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import ModesOperatoiresFlowVisualization from "./ModesOperatoiresFlowVisualization";
import useAnalysisStore from '../../stores/useAnalysisStore';
import useAtelier4Store from '../../stores/useAtelier4Store';
import workshop3Service from "../../services/workshop3Service"; // Use hydration service
import {
  showSuccessToast,
  showErrorToast,
  showLoadingToast,
  updateToast,
} from "../../utils/toastUtils";
import ctiResultsService from "../../services/ctiResultsService";
import {
  saveCTIScenarios,
  loadCTIScenarios,
  deleteCTIScenario,
} from "../../services/operationalScenariosService";
// businessValueService and dreadedEventService removed - now loaded via bulk endpoint
import { filterAttackPathsForScenarios } from "../../utils/ctiFilters";
import CTIFilteringInfo, {
  CTIFilteringInfoCompact,
  CTIFilteringWarning,
} from "./CTIFilteringInfo";
import WorkshopExportMenu from "../common/WorkshopExportMenu";
import Button from "../common/Button";
import scenarioTrackingService from "../../services/scenarioTrackingService";
import ScenarioManager from "./ScenarioManager";
import ScenarioEditModal from "./ScenarioEditModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import PhaseVisualization from "./PhaseVisualization";
import PhaseNavigator from "./PhaseNavigator";
import PhaseDetailModal from "./PhaseDetailModal";
import GuideModal from "./GuideModal";
import SimpleScenarioVisualization from "./SimpleScenarioVisualization";
import {
  processPhaseData,
  enhanceScenarioData,
} from "../../utils/phaseDataProcessor";
import {
  handleScenarioGenerationError,
  handleVisualizationError,
  handleDatabaseError,
  handleGenericError,
  logError,
  createErrorMessage,
} from "../../utils/errorHandling";
import ErrorDisplay, { ErrorBoundary } from "../common/ErrorDisplay";
import {
  ScenarioGenerationLoader,
  PhaseVisualizationLoader,
  SkeletonCard,
  InlineLoader,
  StepProgress,
  LoadingSpinner,
} from "../common/LoadingStates";
import EnhancedLoadingOverlay from "../common/EnhancedLoadingOverlay";
import {
  validateAndSanitize,
  validateAttackPathSelection,
  validateCTIData,
  sanitizeScenario,
} from "../../utils/validation";
import {
  usePerformanceMonitor,
  useMemoryMonitor,
  useMemoizedDataProcessor,
  optimizeScenarioData,
  debounce,
} from "../../utils/performance";
import {
  getAriaLabels,
  useScreenReader,
  useFocusManagement,
  AccessibleButton,
  SkipLink,
} from "../../utils/accessibility";
import { useSafeSave } from "../../hooks/useSafeSave";
import { useSilentSaveListener } from '../../hooks/useSilentSaveListener';
import {
  normalizeScenarios,
  calculateDistributions,
  enrichScenariosWithDreadedEventSeverity,
} from "../../utils/riskLevelNormalizer";
import { calculateVraisemblance } from '../../utils/vraisemblanceCalculator';

const getComputedVraisemblance = (s) => {
  const method = s.riskCalculationMethod?.method;
  const data = s.riskCalculationMethod?.data || {};

  // 1. Modes-based (same as calculateRiskMetrics lines 122-159)
  const modes = data.modes?.length > 0 ? data.modes : s.modesOperatoires || [];
  // Also enter when method is not set but modes exist — matches detectRiskMethod + init useEffect
  if (modes.length > 0 && (!method || method === 'standard' || method === 'advanced')) {
    const retainedModeVs = modes
      .filter((m) => m && m.retained !== false)
      .map((m) => m.vraisemblance)
      .filter((v) => v >= 1 && v <= 4);
    if (retainedModeVs.length === 0) return null;
    return Math.max(...retainedModeVs);
  }

  // 2. Express method (same as calculateRiskMetrics lines 162-170)
  if (method === 'express') {
    if (data.estimationMode === 'direct')
      return data.globalVraisemblance || s.scenarioVraisemblance || 3;
    return calculateVraisemblance(data.scenarioProbability || 3, data.scenarioDifficulty || 2);
  }

  // 3. Legacy standard (same as calculateRiskMetrics lines 174-175)
  if (method === 'standard' && data.stepProbabilities) {
    return Math.min(...data.stepProbabilities.filter(v => v >= 1 && v <= 4));
  }
  // 4. Legacy advanced (same as calculateRiskMetrics lines 177-180)
  if (method === 'advanced' && data.stepProbabilities && data.stepDifficulties) {
    const minPS = Math.min(...data.stepProbabilities.filter(v => v >= 1 && v <= 4));
    const maxDT = Math.max(...data.stepDifficulties.filter(v => v >= 1 && v <= 4));
    return calculateVraisemblance(minPS, maxDT);
  }

  // 5. Fallback (same as calculateRiskMetrics lines 183-184)
  if (s.scenarioVraisemblance) return s.scenarioVraisemblance;
  return null;
};

const OperationalScenariosAI = (props) => {
  const analysisStore = useAnalysisStore();
  const atelier4Store = useAtelier4Store();
  const currentAnalysis = analysisStore.currentAnalysis;
  const ctiItemStatuses = atelier4Store.ctiItemStatuses;
  const { t } = useTranslation();
  const { companyAiEnabled } = useAuth();

  const {
    markDataLoaded,
    markModified,
    canAutoSave,
    clearModified,
    useFormChangeDetector,
  } = useSafeSave("Atelier4CtiScenarios");
  const formChangeRef = useFormChangeDetector();

  const [showGuide, setShowGuide] = useState(false);

  // Handle silent save without toasts
  const handleSilentSave = async () => {
    if (!canAutoSave()) return "skipped";

    if (currentAnalysis?.id && generatedScenarios.length > 0) {
      try {
        console.log(
          "OperationalScenariosAI - Performing silent save of generated scenarios",
        );

        // Enhance newly generated scenarios with strategic scenario references
        const enhancedGeneratedScenarios = generatedScenarios.map(
          (scenario) => {
            const selectedAttackPath = ctiData?.allAttackPaths?.find(
              (path) => path.id === scenario.attackPathId,
            );
            return {
              ...scenario,
              strategicScenarioId: selectedAttackPath?.strategicScenarioId,
              strategicScenarioReference:
                selectedAttackPath?.strategicScenarioReference,
              attackPathId: scenario.attackPathId,
              attackPathReference: scenario.attackPathReference,
              enhancedAt: new Date().toISOString(),
            };
          },
        );

        const result = await saveCTIScenarios(
          currentAnalysis.id,
          enhancedGeneratedScenarios,
          null,
        );
        if (result?.success || result?.componentId) {
          console.log("OperationalScenariosAI - Silent save successful");
          setGeneratedScenarios([]);
          clearModified();
          await loadSavedScenarios();
          return true;
        }
      } catch (error) {
        console.error("OperationalScenariosAI - Silent save error:", error);
        return false;
      }
    }
    return false;
  };

  useSilentSaveListener('atelier4-cti-scenarios', handleSilentSave);

  // Performance monitoring
  const { renderCount, logPerformance } = usePerformanceMonitor(
    "OperationalScenariosAI",
  );
  useMemoryMonitor("OperationalScenariosAI");

  // Accessibility features
  const { announce } = useScreenReader();
  const { saveFocus, restoreFocus, trapFocus } = useFocusManagement();

  const [ctiData, setCtiData] = useState(null);
  const [isLoadingCTI, setIsLoadingCTI] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [generatedScenarios, setGeneratedScenarios] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [businessValues, setBusinessValues] = useState([]);
  const [dreadedEvents, setDreadedEvents] = useState([]);
  const [allAttackPaths, setAllAttackPaths] = useState([]);
  const [pathNotes, setPathNotes] = useState({});
  const [savedNotes, setSavedNotes] = useState({});
  const [savingPathId, setSavingPathId] = useState(null);

  const handleSaveNote = async (pathId) => {
    if (!currentAnalysis?.id) return;
    const note = pathNotes[pathId] || "";
    setSavingPathId(pathId);
    try {
      await ctiResultsService.updateCTINote(currentAnalysis.id, pathId, note);
      setSavedNotes((prev) => ({ ...prev, [pathId]: note }));
    } catch {} finally {
      setSavingPathId(null);
    }
  };
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [editingScenario, setEditingScenario] = useState(null);
  const [editModalTab, setEditModalTab] = useState('general');

  // Scenario tracking state (FR-019)
  const [existingScenarios, setExistingScenarios] = useState([]);
  const [scenarioStats, setScenarioStats] = useState(null);
  const [isLoadingExistingScenarios, setIsLoadingExistingScenarios] =
    useState(false);
  const [filteredCtiData, setFilteredCtiData] = useState(null);
  const [ctiValidation, setCtiValidation] = useState(null);

  // Phase visualization state (FR-024)
  const [showPhaseVisualization, setShowPhaseVisualization] = useState(false);
  const [selectedPhaseScenario, setSelectedPhaseScenario] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [showPhaseDetailModal, setShowPhaseDetailModal] = useState(false);
  const [phaseDetailData, setPhaseDetailData] = useState({
    scenario: null,
    phase: null,
  });

  // Scenario visualization modal state
  const [showScenarioVisualization, setShowScenarioVisualization] =
    useState(false);
  const [scenarioToVisualize, setScenarioToVisualize] = useState(null);

  // Error handling state
  const [currentError, setCurrentError] = useState(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  // Loading states
  const [generationStage, setGenerationStage] = useState("preparing");
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState("");
  const [isGenerationComplete, setIsGenerationComplete] = useState(false);
  const [isLoadingPhaseVisualization, setIsLoadingPhaseVisualization] =
    useState(false);

  // Validation states
  const [validationErrors, setValidationErrors] = useState({});
  const [validationWarnings, setValidationWarnings] = useState({});

  // Delete confirmation modal state
  const [scenarioToDelete, setScenarioToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
  const [isDocxMenuOpen, setIsDocxMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Holds the inline all-modes flow viz's PNG capture function (for fresh report images)
  const inlineVizCaptureRef = useRef(null);

  // Per-scenario capture functions for hidden report graphs, keyed by getScenarioKey
  const reportCaptureRefs = useRef({});
  const [captureScenarios, setCaptureScenarios] = useState([]);

  // Pagination and Scenario Counts State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [scenarioStatus, setScenarioStatus] = useState({});

  // Helper to calculate status distribution for attack path cards (User Request)
  const calculateStatusDistribution = useCallback(
    (vulnerabilities = [], techniques = [], globalStatuses = {}) => {
      const distribution = {
        non_corrige: 0,
        corrige: 0,
        risque_accepte: 0,
        non_pertinent: 0,
      };

      const allItems = [...vulnerabilities, ...techniques];

      allItems.forEach((item) => {
        // Priority: 1. Global context status (real-time), 2. Item's own status (from DB), 3. Default
        const status = globalStatuses[item.id] || item.status || "non_corrige";
        if (distribution[status] !== undefined) {
          distribution[status]++;
        } else {
          distribution.non_corrige++;
        }
      });

      return distribution;
    },
    [],
  );

  // Attack paths eligible for operational scenario generation (excludes preliminary mode)
  // Cross-checks allAttackPaths so stale cached attackPathsWithCTI entries (without
  // the propagated preliminary flag) are still hidden.
  const preliminaryIds = useMemo(() =>
    new Set(
      (allAttackPaths || [])
        .filter((p) => p.preliminary?.active === true)
        .map((p) => String(p.id)),
    ),
    [allAttackPaths],
  );

  const isPreliminary = useCallback((p) =>
    p.preliminary?.active === true || preliminaryIds.has(String(p.id)),
    [preliminaryIds],
  );

  const displayPaths = useMemo(() =>
    (ctiData?.attackPathsWithCTI || []).filter((p) => !isPreliminary(p)),
    [ctiData, isPreliminary],
  );

  // Partial-coverage CTI paths (backend additive fields; absent = full coverage as today)
  const isPartialCtiPath = useCallback((p) =>
    p?.ctiMetadata?.isPartial === true ||
    p?.ctiMetadata?.ctiSource === 'shared-assets-partial',
  []);

  // Resolve analyzed/total/uncovered counts for a path (tolerates missing backend fields)
  const getCtiCoverageInfo = useCallback((p) => {
    const md = p?.ctiMetadata || {};
    const cov = p?.ctiCoverage || {};
    const analyzed = md.analyzedAssets ?? cov.analyzed ?? null;
    const total = md.totalAssets ?? cov.total ?? null;
    const uncoveredNames = md.uncoveredAssetNames ?? cov.uncoveredAssetNames ?? [];
    const uncoveredIds = md.uncoveredAssetIds ?? cov.uncoveredAssetIds ?? [];
    const fallbackTotal = Array.isArray(p?.supportAssets) ? p.supportAssets.length : 0;
    return {
      isPartial: isPartialCtiPath(p),
      analyzed: analyzed ?? (isPartialCtiPath(p) ? null : fallbackTotal),
      total: total ?? fallbackTotal,
      uncoveredNames: Array.isArray(uncoveredNames) ? uncoveredNames : [],
      uncoveredIds: Array.isArray(uncoveredIds) ? uncoveredIds : [],
    };
  }, [isPartialCtiPath]);

  // Count of preliminary-mode attack paths (hidden from W4 cards)
  const preliminaryHiddenCount = useMemo(() =>
    (ctiData?.attackPathsWithCTI || []).filter((p) => isPreliminary(p)).length,
    [ctiData, isPreliminary],
  );

  // Total eligible paths (retained, non-preliminary) for the "Chemins totaux" denominator
  const totalEligiblePaths = useMemo(() => {
    if ((allAttackPaths || []).length > 0) {
      return allAttackPaths.filter((p) => p.preliminary?.active !== true).length;
    }
    return Math.max(
      (ctiData?.totalAttackPaths || 0) - preliminaryHiddenCount,
      displayPaths.length,
    );
  }, [allAttackPaths, ctiData, preliminaryHiddenCount, displayPaths]);

  // Calculate paths with CTI but without operational scenarios (excludes preliminary)
  const pathsWithoutScenarios = useMemo(() => {
    if (!scenarioStatus) return 0;
    return displayPaths.filter((path) => !scenarioStatus[path.id]?.exists).length;
  }, [displayPaths, scenarioStatus]);

  // Trigger markModified when generatedScenarios are updated
  useEffect(() => {
    if (generatedScenarios.length > 0) {
      markModified();
    }
  }, [generatedScenarios, markModified]);

  // Load attack paths, CTI data and EBIOS RM data on component mount
  // Now uses single bulk endpoint for all data
  useEffect(() => {
    if (currentAnalysis) {
      loadAttackPathsAndCTI(); // Loads: attack paths, CTI data, business values, dreaded events
      loadSavedScenarios(); // Load any previously saved scenarios
    }
  }, [currentAnalysis]);

  // Filter CTI data whenever ctiData changes
  useEffect(() => {
    if (ctiData && ctiData.attackPathsWithCTI) {
      console.log(
        "[OperationalScenariosAI] Filtering CTI data for scenario generation...",
      );

      // Reset to first page when data changes
      setCurrentPage(1);

      // Filter attack paths to exclude 'corrigé' items
      const filteredResult = filterAttackPathsForScenarios(
        ctiData.attackPathsWithCTI,
      );

      const filteredData = {
        attackPathsWithCTI: filteredResult.attackPathsWithCTI,
        filterMetadata: filteredResult.filterMetadata,
      };

      console.log("[OperationalScenariosAI] CTI filtering results:", {
        originalPaths: displayPaths.length,
        filteredPaths: filteredResult.attackPathsWithCTI.length,
        originalVulnerabilities:
          filteredResult.filterMetadata.originalVulnerabilities,
        filteredVulnerabilities:
          filteredResult.filterMetadata.totalVulnerabilities,
        originalTechniques: filteredResult.filterMetadata.originalTechniques,
        filteredTechniques: filteredResult.filterMetadata.totalTechniques,
      });

      setFilteredCtiData(filteredData);

      // Validation - attack paths are valid even without specific CTI data
      // The AI can generate generic scenarios when no CVEs/techniques are found
      const hasAnyPaths = filteredResult.attackPathsWithCTI.length > 0;
      const hasCTIData = filteredResult.attackPathsWithCTI.some(
        (path) =>
          (path.vulnerabilities?.length || 0) > 0 ||
          (path.attackTechniques?.length || 0) > 0,
      );

      setCtiValidation({
        isValid: hasAnyPaths, // Valid if we have any attack paths
        hasCTIData: hasCTIData, // Additional flag to indicate if specific CTI data exists
      });

      if (!hasCTIData && hasAnyPaths) {
        console.log(
          "[OperationalScenariosAI] Attack paths found but no specific CTI data. Generic scenarios will be generated.",
        );
      }
    } else {
      setFilteredCtiData(null);
      setCtiValidation(null);
    }
  }, [ctiData]);

  // Listen for PDF export event from parent component
  useEffect(() => {
    const handleExportEvent = () => {
      handleExportPDF();
    };

    window.addEventListener("exportAllScenariosPDF", handleExportEvent);
    return () => {
      window.removeEventListener("exportAllScenariosPDF", handleExportEvent);
    };
  }, [generatedScenarios, existingScenarios, currentAnalysis]);

  // Function to intelligently map CTI assets to real EBIOS RM data
  const mapAssetToEBIOSRM = (assetName, businessValueName) => {
    // Find matching business value
    let matchedBusinessValue = null;
    if (businessValueName && businessValues.length > 0) {
      matchedBusinessValue = businessValues.find(
        (bv) =>
          bv.name.toLowerCase().includes(businessValueName.toLowerCase()) ||
          businessValueName.toLowerCase().includes(bv.name.toLowerCase()),
      );
    }

    // If no direct match, try to match by asset name
    if (!matchedBusinessValue && assetName && businessValues.length > 0) {
      matchedBusinessValue = businessValues.find((bv) =>
        bv.supportAssets?.some(
          (asset) =>
            asset.name.toLowerCase().includes(assetName.toLowerCase()) ||
            assetName.toLowerCase().includes(asset.name.toLowerCase()),
        ),
      );
    }

    // Find relevant dreaded events based on business value or asset
    let relevantDreadedEvents = [];
    if (dreadedEvents.length > 0) {
      relevantDreadedEvents = dreadedEvents.filter((event) => {
        if (matchedBusinessValue) {
          // Match by security pillars
          return matchedBusinessValue.securityPillars?.some(
            (pillar) => event.securityPillar === pillar,
          );
        }
        // Default to confidentiality/integrity events for data assets
        return ["confidentialite", "integrite", "disponibilite"].includes(
          event.securityPillar,
        );
      });
    }

    // Select the most relevant dreaded event (highest severity first)
    const selectedDreadedEvent =
      relevantDreadedEvents.length > 0
        ? relevantDreadedEvents.sort((a, b) => {
            const severityOrder = {
              catastrophic: 5,
              critical: 4,
              major: 3,
              moderate: 2,
              minor: 1,
            };
            return (
              (severityOrder[b.severity] || 0) -
              (severityOrder[a.severity] || 0)
            );
          })[0]
        : null;

    return {
      businessValue: matchedBusinessValue,
      dreadedEvent: selectedDreadedEvent,
      // Generate realistic threat sources based on asset type
      threatSource:
        assetName?.toLowerCase().includes("serveur") ||
        assetName?.toLowerCase().includes("server")
          ? "Cybercriminels ciblant les infrastructures"
          : assetName?.toLowerCase().includes("base") ||
              assetName?.toLowerCase().includes("database")
            ? "Attaquants spécialisés en exfiltration de données"
            : "Menaces cybernétiques avancées",
      // Generate realistic objectives
      objective: selectedDreadedEvent
        ? `Causer l'événement redouté: ${selectedDreadedEvent.name}`
        : matchedBusinessValue
          ? `Compromettre la valeur métier: ${matchedBusinessValue.name}`
          : "Exploitation des vulnérabilités identifiées",
    };
  };

  const loadAttackPathsAndCTI = async () => {
    try {
      setIsLoadingCTI(true);

      // OPTIMIZED: Single API call replaces 8+ separate calls
      // Previously: attack-paths + business-values + cti-operational-scenarios + N×cti-results + dreaded-events
      // Now: operational-scenarios-data = 1 request
      console.log(
        "[OperationalScenariosAI] Loading all data in single bulk request...",
      );
      const response = await workshop3Service.getOperationalScenariosData(
        currentAnalysis.id,
      );

      if (response.success && response.data) {
        const {
          attackPaths,
          attackPathsWithCTI,
          businessValues,
          dreadedEvents,
          summary,
        } = response.data;

        // Store all attack paths in state for dropdown selection
        setAllAttackPaths(attackPaths || []);

        // Store business values and dreaded events
        setBusinessValues(businessValues || []);
        setDreadedEvents(dreadedEvents || []);

        // Process CTI data
        const processedCTIData = {
          attackPathsWithCTI: attackPathsWithCTI || [],
          totalAttackPaths:
            summary?.totalAttackPaths || attackPaths?.length || 0,
          attackPathsWithCTICount:
            summary?.attackPathsWithCTICount || attackPathsWithCTI?.length || 0,
        };

        console.log("[OperationalScenariosAI] Bulk load complete:", {
          totalAttackPaths: processedCTIData.totalAttackPaths,
          attackPathsWithCTI: processedCTIData.attackPathsWithCTICount,
          totalVulnerabilities: summary?.totalVulnerabilities || 0,
          totalTechniques: summary?.totalTechniques || 0,
        });

        setCtiData(processedCTIData);
        const noteSeed = Object.fromEntries(
          (attackPathsWithCTI || []).map((p) => [
            p.id,
            p.ctiMetadata?.note || "",
          ]),
        );
        setPathNotes(noteSeed);
        setSavedNotes(noteSeed);
        markDataLoaded();

        // Also load scenario counts across all paths
        await loadAllScenarioCounts();
      } else {
        console.error("[OperationalScenariosAI] Failed to load data");
        setCtiData({
          attackPathsWithCTI: [],
          totalAttackPaths: 0,
          attackPathsWithCTICount: 0,
        });
      }
    } catch (error) {
      console.error("[OperationalScenariosAI] Error loading data:", error);
      setCtiData({
        attackPathsWithCTI: [],
        vulnerabilities: [],
        techniques: [],
        totalAttackPaths: 0,
        attackPathsWithCTICount: 0,
      });
    } finally {
      setIsLoadingCTI(false);
    }
  };

  /**
   * Fetches all scenarios for the analysis and aggregates counts per attack path
   */
  const loadAllScenarioCounts = async () => {
    if (!currentAnalysis?.id) return;

    try {
      console.log("[OperationalScenariosAI] Fetching all scenario counts...");
      const response = await loadCTIScenarios(currentAnalysis.id);

      // Handle response structure (it's often wrapped in data or scenarios directly)
      const scenariosData =
        response?.scenarios || response?.data?.scenarios || [];

      // Aggregate status (exists + vraisemblance) by attackPathId
      const status = {};
      scenariosData.forEach((scenario) => {
        if (!scenario.attackPathId) return;
        const existing = status[scenario.attackPathId];
        const hasMethod = !!scenario.riskCalculationMethod?.method;
        if (existing && !hasMethod) return;
        const result = getComputedVraisemblance(scenario);
        console.log('[BADGE] load attackPath:', scenario.attackPathId,
          '| method:', scenario.riskCalculationMethod?.method,
          '| globalV:', scenario.riskCalculationMethod?.data?.globalVraisemblance,
          '| estMode:', scenario.riskCalculationMethod?.data?.estimationMode,
          '| modes:', scenario.modesOperatoires?.length,
          '| scenarioV:', scenario.scenarioVraisemblance,
          '| result:', result,
          '| selected:', existing ? 'overwrote (no method)' : 'first/with-method');
        status[scenario.attackPathId] = {
          exists: true,
          vraisemblance: result,
        };
      });

      console.log(
        "[OperationalScenariosAI] Aggregated scenario status:",
        status,
      );
      setScenarioStatus(status);
    } catch (error) {
      console.error(
        "[OperationalScenariosAI] Error loading global scenario status:",
        error,
      );
    }
  };

  // FR-005, FR-006: Single attack path selection only
  const handleAssetToggle = (assetId) => {
    // Clear previous validation errors
    setValidationErrors({});
    setValidationWarnings({});

    // Validate selection before setting
    const newSelection = selectedAssets.includes(assetId) ? [] : [assetId];
    const validationResult = validateAttackPathSelection(
      newSelection,
      ctiData?.attackPathsWithCTI || [],
    );

    if (!validationResult.isValid && newSelection.length > 0) {
      console.error(
        "[OperationalScenariosAI] Attack path selection validation failed:",
        validationResult.errors,
      );
      setValidationErrors({ attackPathSelection: validationResult.errors });
      showErrorToast("Sélection de chemin d'attaque invalide");
      return;
    }

    // Clear generated scenarios when switching attack paths
    console.log(
      "[OperationalScenariosAI] Clearing generated scenarios when switching attack paths",
    );
    setGeneratedScenarios([]);

    // Clear other related states
    setSelectedScenario(null);
    setEditingScenario(null);
    setShowPhaseVisualization(false);
    setSelectedPhaseScenario(null);
    setShowScenarioVisualization(false);
    setScenarioToVisualize(null);

    setSelectedAssets((prev) => {
      // If clicking the same asset, deselect it
      if (prev.includes(assetId)) {
        // Also clear existing scenarios when deselecting
        setExistingScenarios([]);
        setScenarioStats(null);
        return [];
      }
      // Otherwise, select only this asset (single selection)
      return [assetId];
    });

    // Load existing scenarios for the selected attack path (FR-019)
    if (assetId && currentAnalysis) {
      loadExistingScenariosForPath(assetId);
    } else {
      // Clear existing scenarios if no asset is selected
      setExistingScenarios([]);
      setScenarioStats(null);
    }
  };

  // Load existing scenarios for a specific attack path (FR-019)
  const loadExistingScenariosForPath = async (attackPathId) => {
    try {
      setIsLoadingExistingScenarios(true);
      console.log(
        `[ScenarioTracking] Loading existing scenarios for attack path: ${attackPathId}`,
      );

      // Note: loadSavedScenarios is called at startup and after saves, no need to call it here

      // Get existing scenarios for this attack path from CTI operational scenarios
      console.log(
        `[ScenarioTracking] Loading CTI scenarios for analysis ${currentAnalysis.id}...`,
      );
      const ctiScenariosResponse = await loadCTIScenarios(currentAnalysis.id);
      console.log(
        `[ScenarioTracking] CTI scenarios response:`,
        ctiScenariosResponse,
      );

      let existingCTIScenarios = [];

      // Check if we have scenarios in the response (handle both response formats)
      const scenariosData =
        ctiScenariosResponse?.scenarios ||
        ctiScenariosResponse?.data?.scenarios;

      if (scenariosData && Array.isArray(scenariosData)) {
        console.log(
          `[ScenarioTracking] Found ${scenariosData.length} total CTI scenarios`,
        );
        console.log(
          `[ScenarioTracking] All CTI scenarios:`,
          scenariosData.map((s) => ({
            name: s.name,
            attackPathId: s.attackPathId,
            attackPathReference: s.attackPathReference,
          })),
        );

        // Filter scenarios for the selected attack path
        // First, find the selected attack path details to get its reference code
        const selectedAttackPath = allAttackPaths.find(
          (ap) => ap.id === attackPathId,
        );
        const selectedPathReference = selectedAttackPath?.referenceCode;

        console.log(
          `[ScenarioTracking] Looking for scenarios for attack path ${attackPathId}:`,
        );
        console.log(`  - Selected path reference: ${selectedPathReference}`);
        console.log(`  - Selected path name: ${selectedAttackPath?.name}`);

        existingCTIScenarios = scenariosData.filter((scenario) => {
          console.log(
            `[ScenarioTracking] Checking scenario "${scenario.name}":`,
          );
          console.log(`  - scenario.attackPathId: ${scenario.attackPathId}`);
          console.log(
            `  - scenario.attackPathReference: ${scenario.attackPathReference}`,
          );

          // Primary matching: Direct ID match
          const directIdMatch = scenario.attackPathId === attackPathId;

          // Reference matching is NO LONGER used because references like CAD0x and CA0y
          // are dynamically generated based on alphabetical sort order. If the user adds a new
          // source of risk or dreaded event, the sort order shifts, reassigning CAD01 to a completely
          // different attack path. Matching by reference causes scenarios to leak across paths.
          const referenceMatch = false;

          // Tertiary matching: Normalized ID match (handle timestamp differences)
          let normalizedMatch = false;
          if (
            !directIdMatch &&
            !referenceMatch &&
            scenario.attackPathId &&
            attackPathId
          ) {
            // Extract the core part before the final timestamp
            // Keep source-dreadedEvent-ecosystem structure, remove only the final timestamp
            const extractCoreId = (id) => {
              const parts = id.split("-");
              // Keep all parts except the last one (which is typically the timestamp)
              // This preserves the source-dreadedEvent-ecosystem structure
              if (parts.length > 1) {
                return parts.slice(0, -1).join("-");
              }
              return id;
            };

            const scenarioCore = extractCoreId(scenario.attackPathId);
            const selectedCore = extractCoreId(attackPathId);
            // Only match if both cores are substantial (more than just 'source')
            normalizedMatch =
              scenarioCore === selectedCore &&
              scenarioCore.length > 10 &&
              scenarioCore.split("-").length >= 3;

            console.log(`  - Normalized matching:`);
            console.log(`    - scenarioCore: ${scenarioCore}`);
            console.log(`    - selectedCore: ${selectedCore}`);
            console.log(`    - normalizedMatch: ${normalizedMatch}`);
          }

          const finalMatch = directIdMatch || referenceMatch || normalizedMatch;

          console.log(`  - Match results:`);
          console.log(`    - directIdMatch: ${directIdMatch}`);
          console.log(`    - referenceMatch: ${referenceMatch}`);
          console.log(`    - normalizedMatch: ${normalizedMatch}`);
          console.log(`    - finalMatch: ${finalMatch}`);

          return finalMatch;
        });

        console.log(
          `[ScenarioTracking] Filtered CTI scenarios for attack path ${attackPathId}:`,
          existingCTIScenarios.length,
        );

        // Debug: Show available attack paths for reference
        console.log(`[ScenarioTracking] Available attack paths for reference:`);
        allAttackPaths.forEach((ap) => {
          console.log(
            `  - ID: ${ap.id}, Reference: ${ap.referenceCode}, Name: ${ap.name}`,
          );
        });

        // Debug: Show all scenario references
        console.log(`[ScenarioTracking] All scenario references:`);
        scenariosData.forEach((scenario) => {
          console.log(
            `  - Scenario: ${scenario.name}, PathId: ${scenario.attackPathId}, Reference: ${scenario.attackPathReference}`,
          );
        });
      } else {
        console.log(
          `[ScenarioTracking] No CTI scenarios found or invalid response structure`,
        );
      }

      // Also get scenarios from the scenario tracking service (regular operational scenarios)
      let trackingScenarios = [];
      try {
        trackingScenarios =
          await scenarioTrackingService.getScenariosForAttackPath(
            currentAnalysis.id,
            attackPathId,
          );
        // Ensure it's an array
        trackingScenarios = Array.isArray(trackingScenarios)
          ? trackingScenarios
          : [];
      } catch (trackingError) {
        console.log(
          "[ScenarioTracking] No scenarios found in tracking service (this is normal for new attack paths)",
        );
        trackingScenarios = [];
      }

      // Combine both types of scenarios
      const allScenarios = [...existingCTIScenarios, ...trackingScenarios];

      console.log(
        `[ScenarioTracking] Found ${allScenarios.length} existing scenarios for attack path ${attackPathId}:`,
        {
          ctiScenarios: existingCTIScenarios.length,
          trackingScenarios: trackingScenarios.length,
          ctiScenariosDetails: existingCTIScenarios.map((s) => ({
            name: s.name,
            id: s.attackPathId,
          })),
        },
      );

      setExistingScenarios(allScenarios);

      // Update scenario statistics
      if (allScenarios.length > 0) {
        try {
          const stats = await scenarioTrackingService.getScenarioStats(
            currentAnalysis.id,
            attackPathId,
          );
          setScenarioStats(stats);
        } catch (statsError) {
          console.log(
            "[ScenarioTracking] Could not load scenario stats, using basic stats",
          );
          // Create basic stats from available data
          setScenarioStats({
            totalScenarios: allScenarios.length,
            averageProbability:
              allScenarios.reduce(
                (sum, s) => sum + (s.scenarioProbability || s.likelihood || 50),
                0,
              ) / allScenarios.length,
            lastGenerated:
              allScenarios.length > 0 ? new Date().toISOString() : null,
          });
        }
      } else {
        setScenarioStats(null);
      }
    } catch (error) {
      console.error(
        "[ScenarioTracking] Error loading existing scenarios:",
        error,
      );

      // Use comprehensive error handling
      const processedError = handleDatabaseError(error, {
        operation: "database",
        action: "load_scenarios",
        attackPathId: attackPathId,
      });

      // Log error for monitoring
      logError(processedError, {
        component: "OperationalScenariosAI",
        function: "loadExistingScenariosForPath",
        attackPathId: attackPathId,
      });

      // Set empty state
      setExistingScenarios([]);
      setScenarioStats(null);

      // Only show error if it's not a "not found" case
      if (error.response?.status !== 404) {
        const errorMessage = createErrorMessage(processedError, true);
        setCurrentError(errorMessage);
      }
    } finally {
      setIsLoadingExistingScenarios(false);
    }
  };

  const generateOperationalScenarios = async () => {
    // Validate CTI data
    const ctiValidationResult = validateCTIData(ctiData);
    if (!ctiValidationResult.isValid) {
      console.error(
        "[OperationalScenariosAI] CTI data validation failed:",
        ctiValidationResult.errors,
      );
      setValidationErrors({ ctiData: ctiValidationResult.errors });
      showErrorToast("Données CTI invalides pour la génération");
      return;
    }

    // Validate attack path selection
    const selectionValidationResult = validateAttackPathSelection(
      selectedAssets,
      ctiData?.attackPathsWithCTI || [],
    );
    if (!selectionValidationResult.isValid) {
      console.error(
        "[OperationalScenariosAI] Attack path selection validation failed:",
        selectionValidationResult.errors,
      );
      setValidationErrors({
        attackPathSelection: selectionValidationResult.errors,
      });
      showErrorToast(
        "Veuillez sélectionner un chemin d'attaque valide avec des données CTI",
      );
      return;
    }

    // Clear validation errors if all validations pass
    setValidationErrors({});
    setValidationWarnings({});

    // Check if filtered CTI data is available
    if (
      !filteredCtiData ||
      !filteredCtiData.attackPathsWithCTI ||
      filteredCtiData.attackPathsWithCTI.length === 0
    ) {
      showErrorToast(
        "Aucun chemin d'attaque avec données CTI disponible. Effectuez d'abord une analyse CTI.",
      );
      return;
    }

    const toastId = showLoadingToast(
      "Génération des scénarios opérationnels avec IA...",
    );

    try {
      setIsGenerating(true);
      setCurrentError(null);

      // Initialize loading states
      setGenerationStage("preparing");
      setGenerationProgress(10);
      setGenerationMessage("Préparation des données CTI...");

      // Use filtered CTI data for selected attack path (single selection)
      const selectedAttackPathsData = filteredCtiData.attackPathsWithCTI.filter(
        (attackPath) =>
          selectedAssets.includes(attackPath.id) &&
          attackPath.preliminary?.active !== true &&
          !preliminaryIds.has(String(attackPath.id)),
      );

      // Check if any selected paths exist (even without CTI data)
      // Note: We now allow paths without CVEs/techniques - the AI will generate generic scenarios
      const pathsWithData = selectedAttackPathsData.filter(
        (path) =>
          (path.vulnerabilities?.length || 0) > 0 ||
          (path.attackTechniques?.length || 0) > 0,
      );

      // Use paths with CTI data if available, otherwise use all selected paths
      // The backend AI has been updated to handle cases without specific CTI data
      const pathsToUse =
        pathsWithData.length > 0 ? pathsWithData : selectedAttackPathsData;

      if (pathsToUse.length === 0) {
        // This means the attack path doesn't exist in the CTI data at all
        throw new Error(
          "Le chemin d'attaque sélectionné n'est pas trouvé dans les données. Veuillez recharger la page.",
        );
      }

      // Log info about CTI data availability
      if (pathsWithData.length === 0) {
        console.log(
          "[OperationalScenariosAI] No specific CTI data found for selected attack path. AI will generate generic scenarios.",
        );
      }

      // Since we only allow single selection, we should have exactly one path
      const selectedAttackPath = pathsToUse[0];
      console.log(
        `[OperationalScenariosAI] Generating scenario for single attack path: ${selectedAttackPath.pathName} (CTI data: ${pathsWithData.length > 0 ? "available" : "not available"})`,
      );

      // Prepare business context from analysis data (FR-010)
      const businessContext = {
        industry: currentAnalysis?.industry || "Non spécifié",
        companySize: currentAnalysis?.companySize || "Medium",
        riskTolerance: currentAnalysis?.riskTolerance || "Medium",
        securityMaturity: currentAnalysis?.securityMaturity || "Intermediate",
        criticalAssets:
          selectedAttackPath.businessAssets?.map((asset) => asset.name) || [],
      };

      console.log(
        `[OperationalScenariosAI] Business context for ${selectedAttackPath.pathName}:`,
        businessContext,
      );
      console.log(
        `[OperationalScenariosAI] CTI data - Vulnerabilities: ${selectedAttackPath.vulnerabilities?.length || 0}, Techniques: ${selectedAttackPath.attackTechniques?.length || 0}`,
      );

      // Update progress: Sending to AI
      setGenerationStage("generating");
      setGenerationProgress(40);
      setGenerationMessage("Génération du scénario par l'IA...");

      // Call the enhanced AI endpoint V2 for single scenario generation (FR-009)
      // IMPORTANT: The AI endpoint expects vulnerabilities and attackTechniques to be part of the attackPath object
      // This matches how Activity 1 sends data to the AI
      const attackPathWithCTI = {
        ...selectedAttackPath,
        // Ensure vulnerabilities and attackTechniques are directly on the attack path object
        vulnerabilities: selectedAttackPath.vulnerabilities || [],
        attackTechniques: selectedAttackPath.attackTechniques || [],
        // Carry partial-coverage scope through so the AI prompt can state it (additive only)
        ctiMetadata: selectedAttackPath.ctiMetadata,
        ctiCoverage: selectedAttackPath.ctiCoverage,
      };

      console.log(
        "[OperationalScenariosAI] Sending attack path with embedded CTI data to AI:",
        {
          pathName: attackPathWithCTI.pathName,
          vulnerabilitiesCount: attackPathWithCTI.vulnerabilities.length,
          techniquesCount: attackPathWithCTI.attackTechniques.length,
          vulnerabilities: attackPathWithCTI.vulnerabilities.map((v) => ({
            id: v.cveId || v.id,
            severity: v.severity,
          })),
          techniques: attackPathWithCTI.attackTechniques.map((t) => ({
            id: t.id,
            name: t.name,
          })),
        },
      );

      const response = await api.post(
        "/ai/generate-enhanced-operational-scenarios-v2",
        {
          analysisId: currentAnalysis.id,
          attackPath: attackPathWithCTI, // Send attack path with embedded CTI data
          businessContext: businessContext,

          options: {
            detailLevel: "comprehensive",
            includeIndicators: true,
            includeTools: true,
            includeEBIOSPhases: true,
          },
        },
      );

      console.log(
        "[OperationalScenariosAI] Enhanced AI V2 Response:",
        response.data,
      );

      // Check for scenario in response - handle both wrapped and direct formats
      const scenario = response.data?.data?.scenario || response.data?.scenario;

      if (scenario) {
        // Update progress: Processing response
        setGenerationStage("processing");
        setGenerationProgress(70);
        setGenerationMessage("Traitement du scénario généré...");

        // Sanitize scenario data before processing
        let processedScenario = sanitizeScenario(scenario);

        // Validate the generated scenario
        const scenarioValidationResult = validateAndSanitize(
          processedScenario,
          "scenario",
        );
        if (!scenarioValidationResult.isValid) {
          console.warn(
            "[OperationalScenariosAI] Generated scenario has validation issues:",
            scenarioValidationResult.errors,
          );
          setValidationWarnings({
            generatedScenario: scenarioValidationResult.warnings,
          });
          // Continue with warnings but log them
        }

        // Add attack path reference and metadata
        processedScenario.attackPathId = selectedAttackPath.id;
        processedScenario.attackPathReference =
          selectedAttackPath.referenceCode;
        processedScenario.attackPathName = selectedAttackPath.pathName;

        // Optimize scenario data for performance
        const startOptimization = performance.now();
        const optimizedScenarios = optimizeScenarioData([processedScenario], {
          maxScenarios: 50,
          maxStepsPerScenario: 20,
          maxIndicatorsPerStep: 10,
          maxToolsPerStep: 10,
        });
        const endOptimization = performance.now();
        logPerformance(
          "Scenario optimization",
          endOptimization - startOptimization,
        );

        // Set the generated scenario (single scenario for single path)
        setGeneratedScenarios(optimizedScenarios);

        // Update progress: Complete
        setGenerationStage("finalizing");
        setGenerationProgress(100);
        setGenerationMessage("Scénario généré avec succès !");

        // Mark generation as complete to trigger overlay completion
        setIsGenerationComplete(true);

        updateToast(
          toastId,
          `Scénario opérationnel détaillé généré avec succès pour ${selectedAttackPath.pathName}!`,
          "success",
        );

        // Accessibility announcement
        announce(
          `Scénario généré avec succès pour ${selectedAttackPath.pathName}. Le scénario contient ${processedScenario.operationalSteps?.length || 0} étapes opérationnelles.`,
          "polite",
        );

        console.log(
          `[OperationalScenariosAI] Generated enhanced scenario for ${selectedAttackPath.pathName}:`,
          processedScenario,
        );
      } else {
        console.error(
          `[OperationalScenariosAI] Failed to generate scenario for ${selectedAttackPath.pathName}:`,
          response.data,
        );
        throw new Error(
          "L'IA n'a pas pu générer de scénario avec les données filtrées fournies.",
        );
      }
    } catch (error) {
      console.error(
        "[OperationalScenariosAI] Error generating operational scenarios:",
        error,
      );

      // Use comprehensive error handling
      const processedError = handleScenarioGenerationError(error, {
        operation: "generation",
        attackPathId: selectedAssets[0],
        analysisId: currentAnalysis.id,
        ctiDataAvailable: !!filteredCtiData,
      });

      // Log error for monitoring
      logError(processedError, {
        component: "OperationalScenariosAI",
        function: "generateOperationalScenarios",
        attackPathsSelected: selectedAssets.length,
      });

      // Create user-friendly error message
      const errorMessage = createErrorMessage(processedError, true);
      setCurrentError(errorMessage);

      // Show error toast
      updateToast(toastId, processedError.userMessage, "error");
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!scenarioToDelete) return;

    const scenario = scenarioToDelete;
    const scenarioId = scenario._id || scenario.id;
    const scenarioName = scenario.name;

    console.log("[DeleteScenario] Starting delete for scenario:", {
      scenarioId: scenarioId,
      scenarioName: scenarioName,
      hasId: !!scenarioId,
      analysisId: currentAnalysis.id,
      source: scenario.source,
      attackPathId: scenario.attackPathId,
    });

    // Close modal immediately
    setShowDeleteConfirmation(false);
    setScenarioToDelete(null);

    const toastId = showLoadingToast("Suppression du scénario...");

    try {
      // IMMEDIATELY remove from UI (optimistic update)
      console.log("[DeleteScenario] Removing from local state immediately...");
      console.log("[DeleteScenario] Scenario to match:", {
        name: scenarioName,
        attackPathId: scenario.attackPathId,
        hasId: !!scenarioId,
      });

      // Remove by ID if available, otherwise by name and attackPathId
      const matchScenario = (s) => {
        if (scenarioId && (s._id || s.id)) {
          const match = (s._id || s.id) === scenarioId;
          if (match) console.log("[DeleteScenario] Matched by ID:", s.name);
          return match;
        }
        // Fallback: match by name and attackPathId
        const match =
          s.name === scenarioName && s.attackPathId === scenario.attackPathId;
        if (match) {
          console.log("[DeleteScenario] Matched by name+attackPathId:", s.name);
        } else {
          console.log("[DeleteScenario] No match for:", {
            scenarioName: s.name,
            targetName: scenarioName,
            nameMatch: s.name === scenarioName,
            scenarioAttackPathId: s.attackPathId,
            targetAttackPathId: scenario.attackPathId,
            attackPathMatch: s.attackPathId === scenario.attackPathId,
          });
        }
        return match;
      };

      setGeneratedScenarios((prev) => {
        console.log(
          "[DeleteScenario] Generated scenarios before:",
          prev.length,
          prev.map((s) => s.name),
        );
        const filtered = prev.filter((s) => !matchScenario(s));
        console.log(
          `[DeleteScenario] Generated scenarios: ${prev.length} -> ${filtered.length}`,
        );
        return filtered;
      });

      setExistingScenarios((prev) => {
        console.log(
          "[DeleteScenario] Existing scenarios before:",
          prev.length,
          prev.map((s) => s.name),
        );
        const filtered = prev.filter((s) => !matchScenario(s));
        console.log(
          `[DeleteScenario] Existing scenarios: ${prev.length} -> ${filtered.length}`,
        );
        return filtered;
      });

      // Only try to delete from database if scenario has an ID
      if (scenarioId) {
        console.log(
          "[DeleteScenario] Deleting scenario from database with ID:",
          scenarioId,
        );

        // Determine if this is a CTI scenario or regular scenario
        const isCTIScenario =
          scenario.source === "ai_generated_enhanced" ||
          scenario.source === "ai_generated" ||
          scenario.metadata?.generatedByAI ||
          scenario.attackPathId;

        // Delete from database
        let deleteResponse;
        if (isCTIScenario) {
          console.log(
            "[DeleteScenario] Deleting as CTI scenario from database...",
          );
          deleteResponse = await deleteCTIScenario(
            currentAnalysis.id,
            scenarioId,
          );
        } else {
          console.log(
            "[DeleteScenario] Deleting as regular operational scenario from database...",
          );
          deleteResponse = await scenarioTrackingService.deleteScenario(
            currentAnalysis.id,
            scenarioId,
          );
        }

        console.log(
          "[DeleteScenario] Database delete response:",
          deleteResponse,
        );
      } else {
        // No ID - need to find and delete by name and attackPathId from database
        console.log(
          "[DeleteScenario] No ID - attempting to delete from database by name and attackPathId...",
        );

        try {
          // Load all scenarios from database
          const ctiResponse = await loadCTIScenarios(currentAnalysis.id);
          const allScenarios =
            ctiResponse?.data?.scenarios || ctiResponse?.scenarios || [];

          console.log(
            "[DeleteScenario] Loaded scenarios from database:",
            allScenarios.length,
          );
          console.log(
            "[DeleteScenario] Looking for scenario with name:",
            scenarioName,
          );
          console.log(
            "[DeleteScenario] Looking for attackPathId:",
            scenario.attackPathId,
          );
          console.log(
            "[DeleteScenario] Database scenarios:",
            allScenarios.map((s) => ({
              name: s.name,
              attackPathId: s.attackPathId,
              _id: s._id,
              nameMatch: s.name === scenarioName,
              attackPathMatch: s.attackPathId === scenario.attackPathId,
            })),
          );

          // Find the scenario by name and attackPathId
          const scenarioToDeleteFromDB = allScenarios.find(
            (s) =>
              s.name === scenarioName &&
              s.attackPathId === scenario.attackPathId,
          );

          if (scenarioToDeleteFromDB) {
            console.log("[DeleteScenario] Found scenario in database");
            console.log("[DeleteScenario] Deleting by name:", scenarioName);

            // Scenarios don't have individual _id - use name as identifier
            // Backend deleteCTIOperationalScenario finds scenario by name in array
            const deleteResponse = await deleteCTIScenario(
              currentAnalysis.id,
              scenarioName,
            );
            console.log(
              "[DeleteScenario] Database delete response:",
              deleteResponse,
            );
          } else {
            console.log("[DeleteScenario] Scenario not found in database");
          }
        } catch (dbError) {
          console.error(
            "[DeleteScenario] Error deleting from database:",
            dbError,
          );
          // Continue anyway - at least it's removed from local state
        }
      }

      // Don't reload immediately - we already updated local state optimistically
      // Reloading too soon causes the deleted scenario to reappear before DB update propagates
      console.log(
        "[DeleteScenario] Delete completed successfully - using optimistic UI update",
      );

      // Update global counts in background
      loadAllScenarioCounts();

      updateToast(toastId, "Scénario supprimé avec succès", "success");
    } catch (error) {
      console.error("[DeleteScenario] Delete failed:", error);
      console.error("[DeleteScenario] Error details:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });

      // If delete failed, reload to restore correct state
      if (selectedAssets.length > 0) {
        console.log("[DeleteScenario] Reloading scenarios after error...");
        await loadExistingScenariosForPath(selectedAssets[0]);
      }

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Erreur inconnue lors de la suppression";
      updateToast(toastId, `Erreur: ${errorMessage}`, "error");
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setScenarioToDelete(null);
  };

  // Handle phase visualization (FR-024)
  const handlePhaseVisualization = (scenario, phase = null) => {
    try {
      setIsLoadingPhaseVisualization(true);
      console.log(
        `[PhaseVisualization] Opening visualization for scenario: ${scenario.name}, phase: ${phase || "all"}`,
      );
      console.log(
        `[PhaseVisualization] Scenario has ${scenario.operationalSteps?.length || 0} operational steps`,
      );

      // Validate scenario data
      if (!scenario) {
        throw new Error("Aucun scénario fourni pour la visualisation");
      }

      if (
        !scenario.operationalSteps ||
        scenario.operationalSteps.length === 0
      ) {
        throw new Error(
          "Le scénario ne contient pas d'étapes opérationnelles à visualiser",
        );
      }

      if (
        phase &&
        !scenario.operationalSteps.find((step) => step.phase === phase)
      ) {
        throw new Error(`La phase "${phase}" n'existe pas dans ce scénario`);
      }

      // If visualization is already open, close it first to force a re-render
      if (showPhaseVisualization) {
        console.log(
          "[PhaseVisualization] Closing existing visualization before opening new one",
        );
        setShowPhaseVisualization(false);
        setSelectedPhaseScenario(null);
        setSelectedPhase(null);

        // Wait a brief moment before reopening to ensure complete unmount/remount
        setTimeout(() => {
          openPhaseVisualization(scenario, phase);
        }, 100);
      } else {
        // No existing visualization, open directly
        openPhaseVisualization(scenario, phase);
      }
    } catch (error) {
      console.error("[PhaseVisualization] Error opening visualization:", error);

      // Use comprehensive error handling
      const processedError = handleVisualizationError(error, {
        operation: "visualization",
        scenarioId: scenario?.id || scenario?._id,
        phase: phase,
        hasOperationalSteps: !!scenario?.operationalSteps?.length,
      });

      // Log error for monitoring
      logError(processedError, {
        component: "OperationalScenariosAI",
        function: "handlePhaseVisualization",
        scenarioName: scenario?.name,
      });

      // Create user-friendly error message
      const errorMessage = createErrorMessage(processedError, true);
      setCurrentError(errorMessage);

      // Show error toast
      showErrorToast(processedError.userMessage);
    }
  };

  // Helper function to open phase visualization
  const openPhaseVisualization = (scenario, phase) => {
    // Enhance scenario data for visualization
    const enhancedScenario = enhanceScenarioData(scenario, {
      includeAssets: true,
      includeVulnerabilities: true,
      maxIndicatorsPerPhase: 5,
      maxToolsPerPhase: 5,
    });

    console.log(
      `[PhaseVisualization] Enhanced scenario ready for visualization`,
    );
    setSelectedPhaseScenario(enhancedScenario);
    setSelectedPhase(phase);
    setShowPhaseVisualization(true);

    // Clear any previous errors
    setCurrentError(null);

    // Complete loading and scroll to visualization
    setTimeout(() => {
      setIsLoadingPhaseVisualization(false);

      // Scroll to visualization section after it's rendered
      setTimeout(() => {
        const visualizationSection = document.getElementById(
          "phase-visualization-section",
        );
        if (visualizationSection) {
          visualizationSection.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
          console.log("[PhaseVisualization] Scrolled to visualization section");
        }
      }, 150); // Additional delay to ensure DOM is fully rendered
    }, 500); // Small delay for smooth UX
  };

  // Handle phase detail modal
  const handlePhaseDetail = (scenario, phase) => {
    console.log(`[PhaseVisualization] Opening phase detail for: ${phase}`);
    setPhaseDetailData({ scenario, phase });
    setShowPhaseDetailModal(true);
  };

  // Close phase visualization
  const closePhaseVisualization = () => {
    setShowPhaseVisualization(false);
    setSelectedPhaseScenario(null);
    setSelectedPhase(null);
  };

  // Close phase detail modal
  const closePhaseDetailModal = () => {
    setShowPhaseDetailModal(false);
    setPhaseDetailData({ scenario: null, phase: null });
  };

  // Handle scenario visualization inline
  const handleViewScenario = (scenario) => {
    console.log(
      "[OperationalScenariosAI] Showing scenario visualization for:",
      scenario.name,
    );
    console.log("[OperationalScenariosAI] Scenario data:", scenario);
    console.log(
      "[OperationalScenariosAI] Operational steps:",
      scenario.operationalSteps,
    );
    console.log(
      "[OperationalScenariosAI] Operational steps length:",
      scenario.operationalSteps?.length,
    );
    console.log(
      "[OperationalScenariosAI] First step:",
      scenario.operationalSteps?.[0],
    );
    setScenarioToVisualize(scenario);
    setShowScenarioVisualization(true);
  };

  // Hide scenario visualization
  const closeScenarioVisualization = () => {
    setShowScenarioVisualization(false);
    setScenarioToVisualize(null);
  };

  // Handle phase change in visualization
  const handlePhaseChange = (newPhase) => {
    setSelectedPhase(newPhase);
  };

  // Handle editing a scenario
  const handleEditScenario = (scenario) => {
    setEditModalTab('general');
    const defaultSteps = [];

    const editingData = {
      ...scenario,
      operationalSteps:
        scenario.operationalSteps || scenario.steps || defaultSteps,
      modesOperatoires: scenario.modesOperatoires || [],
    };

    setEditingScenario(editingData);
  };

  const handleAddMode = (scenario) => {
    setEditModalTab('modes');
    const defaultSteps = [];
    setEditingScenario({
      ...scenario,
      operationalSteps: scenario.operationalSteps || scenario.steps || defaultSteps,
      modesOperatoires: scenario.modesOperatoires || [],
    });
  };

  const handleDeleteMode = async (scenarioId, modeId) => {
    const updated = existingScenarios.map(s => {
      const key = getScenarioKey(s);
      if (key === scenarioId || s.id === scenarioId || s._id === scenarioId) {
        return { ...s, modesOperatoires: (s.modesOperatoires || []).filter(m => m.id !== modeId) };
      }
      return s;
    });
    setExistingScenarios(updated);
    try {
      await saveCTIScenarios(currentAnalysis.id, updated.map(ensureScenarioId), null);
    } catch (err) {
      console.error('[DeleteMode] Save failed:', err);
      await loadSavedScenarios();
    }
  };

  // Toggle the retenu flag on a mode and recompute scenario vraisemblance from retained modes
  const handleToggleRetained = async (updatedScenario) => {
    const key = getScenarioKey(updatedScenario);
    const updated = existingScenarios.map(s =>
      getScenarioKey(s) === key ? updatedScenario : s
    );
    setExistingScenarios(updated);
    try {
      await saveCTIScenarios(currentAnalysis.id, updated.map(ensureScenarioId), null);
    } catch (err) {
      console.error('[ToggleRetained] Save failed:', err);
      await loadSavedScenarios();
    }
  };

  // Persist the general AI security critique on the scenario (rides the wholesale save)
  const handleSaveCritique = async (updatedScenario) => {
    const critique = updatedScenario.securityCritique;
    const updated = existingScenarios.map(s =>
      getScenarioKey(s) === getScenarioKey(updatedScenario)
        ? { ...s, securityCritique: critique }
        : s
    );
    setExistingScenarios(updated);
    try {
      await saveCTIScenarios(currentAnalysis.id, updated.map(ensureScenarioId), null);
      showSuccessToast("Critique de sécurité enregistrée");
    } catch (err) {
      console.error('[SaveCritique] Save failed:', err);
      await loadSavedScenarios();
    }
  };

  // Save edited scenario (or new manual scenario) - PERSISTS TO DATABASE
  // Identity key for scenarios: id || name+attackPathId
  // Scenarios stored in Mixed fields may lack _id/id entirely,
  // so name+attackPathId is the reliable fallback identity.
  const getScenarioKey = (s) => {
    if (s.id) return s.id;
    if (s._id) return s._id;
    return `${s.name}::${s.attackPathId}`;
  };

  // Assign a stable id to a scenario if it lacks both _id and id.
  // Uses attackPathId + name hash so the id is deterministic across sessions.
  const ensureScenarioId = (s) => {
    if (s.id) return s;
    if (s._id) return { ...s, id: s._id };
    // Generate deterministic id from attackPathId + name
    const stableId = `scenario_${s.attackPathId}_${s.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').substring(0, 40)}`;
    return { ...s, id: stableId };
  };

  const saveEditedScenario = async (savedScenario) => {
    if (!savedScenario) return;

    const toastId = showLoadingToast("Sauvegarde en cours...");

    try {
      const vraisemblanceToLikelihood = (v) => {
        if (v >= 4) return "Très élevé";
        if (v >= 3) return "Élevé";
        if (v >= 2) return "Moyen";
        return "Faible";
      };

      const vraisemblanceToProbability = (v) => {
        if (v >= 4) return 95;
        if (v >= 3) return 75;
        if (v >= 2) return 50;
        return 25;
      };

      const enhancedScenario = ensureScenarioId({
        ...savedScenario,
        likelihood: vraisemblanceToLikelihood(
          savedScenario.scenarioVraisemblance || 2,
        ),
        scenarioProbability: vraisemblanceToProbability(
          savedScenario.scenarioVraisemblance || 2,
        ),
        attackPathReference:
          savedScenario.attackPathId && allAttackPaths.length > 0
            ? allAttackPaths.find((ap) => ap.id === savedScenario.attackPathId)
                ?.referenceCode
            : undefined,
      });

      // Before editing, the scenario was loaded with its original key.
      // We need to match against the ORIGINAL key (before name change),
      // not the new key (after name change). The edit modal preserves
      // the original id/_id, so getScenarioKey should find a match.
      const scenarioId = getScenarioKey(enhancedScenario);

      // Check if this scenario already exists in either array
      const isUpdate =
        generatedScenarios.some((s) => getScenarioKey(s) === scenarioId) ||
        existingScenarios.some((s) => getScenarioKey(s) === scenarioId);

      // Build the full list of all scenarios to send to the backend.
      // Key insight: after initial save, generatedScenarios is empty and
      // all scenarios are in existingScenarios. So we primarily use
      // existingScenarios. We update the matching scenario in-place.
      let allScenarios;
      if (isUpdate) {
        allScenarios = existingScenarios.map((s) =>
          getScenarioKey(s) === scenarioId ? enhancedScenario : s
        );
        // Also update generatedScenarios if it contains the scenario
        setGeneratedScenarios((prev) =>
          prev.map((s) => getScenarioKey(s) === scenarioId ? enhancedScenario : s)
        );
        setExistingScenarios((prev) =>
          prev.map((s) => getScenarioKey(s) === scenarioId ? enhancedScenario : s)
        );
      } else {
        allScenarios = [...existingScenarios, enhancedScenario];
        setGeneratedScenarios((prev) => [...prev, enhancedScenario]);
        setExistingScenarios((prev) => [...prev, enhancedScenario]);
      }

      // Ensure every scenario has a stable id before sending to backend
      const scenariosWithIds = allScenarios.map(ensureScenarioId);

      // Deduplicate by getScenarioKey
      const uniqueScenarios = [];
      const seenKeys = new Set();
      scenariosWithIds.forEach((scenario) => {
        const key = getScenarioKey(scenario);
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          uniqueScenarios.push(scenario);
        }
      });

      const result = await saveCTIScenarios(
        currentAnalysis.id,
        uniqueScenarios,
        null,
      );

      if (result?.success || result?.componentId) {
        updateToast(
          toastId,
          isUpdate
            ? "Scénario modifié et sauvegardé"
            : "Scénario créé et sauvegardé",
          "success",
        );

        setGeneratedScenarios([]);
        await loadSavedScenarios();
        setEditingScenario(null);
      } else {
        throw new Error(result?.message || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("[SaveEditedScenario] Error saving to database:", error);
      updateToast(
        toastId,
        `Erreur: ${error.message || "Impossible de sauvegarder"}`,
        "error",
      );
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingScenario(null);
  };

  // Handle manual scenario creation - Opens the modal with a template
  const handleAddManualScenario = () => {
    const newScenarioTemplate = {
      id: `manual_${Date.now()}`,
      name: "",
      description: "",
      operationalSteps: [],
      modesOperatoires: [],
      scenarioVraisemblance: 1,
      isManual: true,
      attackPathId: selectedAssets[0] || "", // Pre-select attack path if available
      severity: "Moyen",
      timeline: "1-7 jours",
      requiredSkills: "Intermédiaire",
      detectionDifficulty: "Moyen",
    };
    setEditingScenario(newScenarioTemplate);
  };

  // Legacy saveManualScenario removed as we use saveEditedScenario now

  // Cancel manual scenario creation

  // Load saved scenarios (simplified - no React Flow state)
  // Returns the loaded scenarios array for use in export handlers
  const loadSavedScenarios = async () => {
    if (!currentAnalysis?.id) return [];

    try {
      console.log("[OperationalScenariosAI] Loading saved CTI scenarios...");
      const result = await loadCTIScenarios(currentAnalysis.id);

      console.log("[OperationalScenariosAI] CTI scenarios response:", result);

      // Handle different response formats
      let scenarios = [];

      if (result?.data?.scenarios) {
        // New format: { data: { scenarios: [...] } }
        scenarios = result.data.scenarios;
      } else if (result?.scenarios) {
        // Direct format: { scenarios: [...] }
        scenarios = result.scenarios;
      } else if (Array.isArray(result)) {
        // Array format: [...]
        scenarios = result;
      }

      if (scenarios && scenarios.length > 0) {
        console.log(
          `[DIAG-load] DB returned ${scenarios.length} scenarios:`,
          scenarios.map((s) => ({
            _id: s._id,
            id: s.id,
            key: getScenarioKey(s),
            name: s.name,
            attackPathId: s.attackPathId,
          })),
        );

        // Store all scenarios in existingScenarios, not generatedScenarios
        // This prevents duplication and allows proper filtering per attack path

        // Remove duplicates from loaded scenarios first
        const uniqueScenarios = [];
        const seenKeys = new Set();

        scenarios.forEach((scenario) => {
          const ensured = ensureScenarioId(scenario);
          const clean = { ...ensured };
          const key = getScenarioKey(clean);
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            uniqueScenarios.push(clean);
          } else {
            console.log(
              `[OperationalScenariosAI] Skipping duplicate scenario: ${scenario.name} (${key})`,
            );
          }
        });

        console.log(
          `[OperationalScenariosAI] Removed ${scenarios.length - uniqueScenarios.length} duplicates from loaded scenarios`,
        );

        // Replace existing scenarios with the cleaned unique scenarios
        setExistingScenarios(uniqueScenarios);

        console.log(
          `[OperationalScenariosAI] Loaded ${scenarios.length} saved scenarios successfully`,
        );

        return uniqueScenarios; // Return for export handlers
      } else {
        console.log("[OperationalScenariosAI] No saved scenarios found");
        setExistingScenarios([]);
        return [];
      }
    } catch (error) {
      console.log(
        "[OperationalScenariosAI] No saved scenarios found or error loading:",
        error.message,
      );
      // This is expected if no scenarios have been saved yet
      setExistingScenarios([]);
      return [];
    }
  };

  const saveScenarios = async () => {
    console.log("🚀 [SAVE] Starting save process...");

    if (generatedScenarios.length === 0) {
      console.log("❌ [SAVE] No new scenarios to save");
      showErrorToast("Aucun nouveau scénario à sauvegarder");
      return;
    }

    console.log("✅ [SAVE] Pre-checks passed, starting save...");
    const toastId = showLoadingToast(
      `Sauvegarde de ${generatedScenarios.length} nouveaux scénarios...`,
    );

    try {
      // Load ALL existing scenarios from database first to avoid overwriting
      console.log("📊 [SAVE] Loading all existing scenarios from database...");
      let allExistingScenarios = [];

      try {
        const existingScenariosResponse = await loadCTIScenarios(
          currentAnalysis.id,
        );
        const scenariosData =
          existingScenariosResponse?.scenarios ||
          existingScenariosResponse?.data?.scenarios;
        if (scenariosData && Array.isArray(scenariosData)) {
          allExistingScenarios = scenariosData;
          console.log(
            `📊 [SAVE] Found ${allExistingScenarios.length} existing scenarios in database`,
          );
        }
      } catch (loadError) {
        console.log(
          "📊 [SAVE] No existing scenarios found or error loading (this is normal for first save):",
          loadError.message,
        );
        allExistingScenarios = [];
      }

      // Enhance newly generated scenarios with strategic scenario references
      const enhancedGeneratedScenarios = generatedScenarios.map((scenario) => {
        // Find the strategic scenario that contains this attack path
        const selectedAttackPath = ctiData?.allAttackPaths?.find(
          (path) => path.id === scenario.attackPathId,
        );

        return {
          ...scenario,
          // Add strategic scenario references for easier matching in Atelier 5
          strategicScenarioId: selectedAttackPath?.strategicScenarioId,
          strategicScenarioReference:
            selectedAttackPath?.strategicScenarioReference,
          // Keep existing attack path references
          attackPathId: scenario.attackPathId,
          attackPathReference: scenario.attackPathReference,
          // Add timestamp for tracking
          enhancedAt: new Date().toISOString(),
        };
      });

      // Only send the newly generated scenarios - let backend handle merging with existing ones
      const scenariosToSave = enhancedGeneratedScenarios;

      console.log("🚀 [SAVE] Save operation details:", {
        hasGeneratedScenarios: !!generatedScenarios,
        generatedScenariosCount: generatedScenarios?.length || 0,
        allExistingScenariosCount: allExistingScenarios?.length || 0,
        newScenariosToSave: scenariosToSave.length,
        hasCurrentAnalysis: !!currentAnalysis,
        analysisId: currentAnalysis?.id,
      });

      console.log("📊 [SAVE] New scenarios data to save:", {
        enhancedGeneratedScenarios: enhancedGeneratedScenarios.map((s) => ({
          name: s.name,
          attackPathId: s.attackPathId,
          attackPathReference: s.attackPathReference,
          strategicScenarioId: s.strategicScenarioId,
          strategicScenarioReference: s.strategicScenarioReference,
          generationAttempt: s.generationAttempt,
        })),
        totalNewCount: scenariosToSave.length,
      });

      console.log("🎯 [SAVE] Final payload preview:", {
        analysisId: currentAnalysis.id,
        newScenariosCount: scenariosToSave.length,
        existingCount: allExistingScenarios.length,
        note: "Backend will merge new scenarios with existing ones",
      });

      console.log("🌐 [SAVE] Calling saveCTIScenarios service...");
      const result = await saveCTIScenarios(
        currentAnalysis.id,
        scenariosToSave,
        null,
      );

      console.log("📥 [SAVE] Service response received:", result);
      console.log("📥 [SAVE] Response type:", typeof result);
      console.log("📥 [SAVE] Response properties:", Object.keys(result || {}));
      console.log("📥 [SAVE] Response.success:", result?.success);
      console.log("📥 [SAVE] Response.message:", result?.message);
      console.log(
        "📥 [SAVE] Scenarios saved count from response:",
        result?.data?.scenariosCount || result?.scenariosCount,
      );
      console.log("📥 [SAVE] New scenarios we sent:", scenariosToSave.length);

      // Handle both response formats:
      // New format: { success: true, message: "...", data: {...} }
      // Old format: { componentId: "...", savedAt: "...", scenariosCount: ..., version: ... }
      const isNewFormat = result && typeof result.success === "boolean";
      const isOldFormat =
        result &&
        result.componentId &&
        result.savedAt &&
        typeof result.scenariosCount === "number";

      if (isNewFormat && result.success === true) {
        console.log("✅ [SAVE] Save successful (new format)!");
        const totalSaved =
          result.data?.scenariosCount || scenariosToSave.length;
        const message = `${enhancedGeneratedScenarios.length} nouveau${enhancedGeneratedScenarios.length > 1 ? "x" : ""} scénario${enhancedGeneratedScenarios.length > 1 ? "s" : ""} ajouté${enhancedGeneratedScenarios.length > 1 ? "s" : ""} avec succès (Total: ${totalSaved})`;

        // Refresh counts to update badges in the grid
        loadAllScenarioCounts();

        updateToast(toastId, message, "success");

        // Clear generated scenarios after successful save and reload all scenarios
        console.log(
          "✅ [SAVE] Clearing generated scenarios and reloading all scenarios...",
        );
        console.log(
          "✅ [SAVE] Enhanced scenarios with strategic references:",
          enhancedGeneratedScenarios.map((s) => ({
            name: s.name,
            strategicScenarioId: s.strategicScenarioId,
            strategicScenarioReference: s.strategicScenarioReference,
          })),
        );
        setGeneratedScenarios([]);

        // Reload all saved scenarios to get the updated list
        await loadSavedScenarios();

        // Also reload existing scenarios for current path if one is selected
        if (selectedAssets.length > 0) {
          loadExistingScenariosForPath(selectedAssets[0]);
        }
      } else if (isOldFormat) {
        console.log("✅ [SAVE] Save successful (old format)!");
        const totalSaved = result.scenariosCount || scenariosToSave.length;
        const message = `${enhancedGeneratedScenarios.length} nouveau${enhancedGeneratedScenarios.length > 1 ? "x" : ""} scénario${enhancedGeneratedScenarios.length > 1 ? "s" : ""} ajouté${enhancedGeneratedScenarios.length > 1 ? "s" : ""} avec succès (Total: ${totalSaved})`;
        updateToast(toastId, message, "success");

        // Clear generated scenarios after successful save and reload all scenarios
        console.log(
          "✅ [SAVE] Clearing generated scenarios and reloading all scenarios...",
        );
        setGeneratedScenarios([]);

        // Reload all saved scenarios to get the updated list
        await loadSavedScenarios();

        // Also reload existing scenarios for current path if one is selected
        if (selectedAssets.length > 0) {
          loadExistingScenariosForPath(selectedAssets[0]);
        }
      } else {
        console.log("❌ [SAVE] Save failed - invalid response format");
        console.log("❌ [SAVE] Full result object:", result);
        throw new Error(result?.message || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("❌ [SAVE] Error in save process:", error);
      console.error("❌ [SAVE] Error type:", typeof error);
      console.error("❌ [SAVE] Error constructor:", error.constructor.name);
      console.error("❌ [SAVE] Full error object:", error);
      console.error("❌ [SAVE] Error properties:", {
        message: error.message,
        status: error.status,
        data: error.data,
        stack: error.stack,
        name: error.name,
        cause: error.cause,
      });

      // Try to extract more error details
      if (error.response) {
        console.error("❌ [SAVE] HTTP Response error:", error.response);
      }
      if (error.request) {
        console.error("❌ [SAVE] HTTP Request error:", error.request);
      }

      const errorMessage =
        error.data?.message ||
        error.message ||
        "Erreur inconnue lors de la sauvegarde";
      console.log("❌ [SAVE] Final error message:", errorMessage);
      updateToast(
        toastId,
        `Erreur lors de la sauvegarde: ${errorMessage}`,
        "error",
      );
    }
  };

  // Export ALL scenarios to PDF
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      // Reload ALL saved scenarios and get them directly (not from state which may be filtered)
      const allSavedScenarios = await loadSavedScenarios();

      // Get ALL scenarios (generated + all saved) across ALL attack paths
      const rawScenarios = [...generatedScenarios, ...allSavedScenarios];

      if (rawScenarios.length === 0) {
        showErrorToast("Aucun scénario à exporter");
        return;
      }

      // Enrich scenarios with attack path context and override severity from dreaded events
      const allScenarios = rawScenarios.map((scenario) => {
        const attackPathId = scenario.attackPathId;
        const attackPath =
          allAttackPaths.find((ap) => ap.id === attackPathId) || {};
        const ctiPath = (ctiData?.attackPathsWithCTI || []).find(
          (ap) => ap.id === attackPathId,
        );

        return {
          ...scenario,
          context: {
            sourceRisk: attackPath.sourceRiskName || "Non spécifié",
            targetObjective:
              attackPath.objectifVise ||
              attackPath.targetObjective ||
              "Non spécifié",
            dreadedEvent: attackPath.dreadedEventName || "Non spécifié",
            businessAssets: attackPath.businessAssets || [],
            ctiMetadata: ctiPath?.ctiMetadata,
            ctiCoverage: ctiPath?.ctiCoverage,
          },
        };
      });

      // Override scenario severity with dreaded event severity from attack paths
      const enrichedScenarios = enrichScenariosWithDreadedEventSeverity(
        allScenarios,
        allAttackPaths,
        dreadedEvents,
      );

      // Per-scenario fresh PNGs — each scenario gets its own combined graph
      const scenariosToCapture = enrichedScenarios.filter(
        (s) => (s.modesOperatoires || []).length > 0,
      );
      if (scenariosToCapture.length > 0) {
        setCaptureScenarios(scenariosToCapture);
        // Wait for the hidden graphs to mount and fitView
        await new Promise((r) => setTimeout(r, 600));
        for (const s of scenariosToCapture) {
          const key = getScenarioKey(s);
          const png = reportCaptureRefs.current[key]
            ? await reportCaptureRefs.current[key]()
            : null;
          console.log('[EXPORT-PNG]', key, png ? { len: png.dataUrl?.length, w: png.width, h: png.height } : null);
          // ponytail: 10KB floor = blank-image guard; real graphs are 100KB+
          if (png && png.dataUrl && png.dataUrl.length > 10000 && png.width > 0 && png.height > 0) {
            s.vizPng = png.dataUrl;
            s.vizPngWidth = png.width;
            s.vizPngHeight = png.height;
          }
        }
        setCaptureScenarios([]);
      }

      // DEBUG: Log raw and enriched severity values
      console.log("[OperationalScenariosAI EXPORT] Severity enrichment:");
      enrichedScenarios.forEach((s, i) => {
        console.log(
          `  [${i}] name=${s.name?.substring(0, 40)} severity=${JSON.stringify(s.severity)} source=${s.severitySource} likelihood=${JSON.stringify(s.likelihood)}`,
        );
      });

      // Import PDF generator dynamically
      const OperationalScenariosPdfGenerator = (
        await import("../../utils/operationalScenariosPdfGenerator")
      ).default;

      // Normalize scenarios and calculate statistics
      const normalizedScenarios = normalizeScenarios(enrichedScenarios);
      const { severityDistribution, likelihoodDistribution } =
        calculateDistributions(enrichedScenarios);

      // Count unique attack paths
      const uniqueAttackPathsPdf = new Set(
        enrichedScenarios
          .map((s) => s.attackPathId || s.attackPathReference)
          .filter(Boolean),
      );

      const reportData = {
        scenarios: normalizedScenarios,
        summary: {
          totalScenarios: enrichedScenarios.length,
          totalAttackPaths: uniqueAttackPathsPdf.size,
          criticalScenarios: severityDistribution.critical,
          highSeverityScenarios: severityDistribution.high,
        },
        severityDistribution,
        likelihoodDistribution,
      };

      const analysisData = {
        id: currentAnalysis?.id,
        name: currentAnalysis?.name,
        companyName: currentAnalysis?.companyName,
      };

      const pdfGenerator = new OperationalScenariosPdfGenerator(
        reportData,
        analysisData,
      );
      const pdfBlob = await pdfGenerator.generateReport();

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `scenarios-operationnels-${currentAnalysis?.name || "analyse"}-${new Date().toISOString().split("T")[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      showSuccessToast("PDF exporté avec succès");
    } catch (error) {
      console.error("Erreur lors de l'exportation PDF:", error);
      showErrorToast("Erreur lors de l'exportation PDF");
    } finally {
      setIsExporting(false);
    }
  };

  // Export ALL scenarios to PDF (English)
  const handleExportPDFEn = async () => {
    setIsExporting(true);
    try {
      // Reload ALL saved scenarios and get them directly (not from state which may be filtered)
      const allSavedScenarios = await loadSavedScenarios();

      // Get ALL scenarios (generated + all saved) across ALL attack paths
      const rawScenarios = [...generatedScenarios, ...allSavedScenarios];

      if (rawScenarios.length === 0) {
        showErrorToast("No scenarios to export");
        return;
      }

      // Enrich scenarios with attack path context
      const allScenarios = rawScenarios.map((scenario) => {
        const attackPathId = scenario.attackPathId;
        const attackPath =
          allAttackPaths.find((ap) => ap.id === attackPathId) || {};
        const ctiPath = (ctiData?.attackPathsWithCTI || []).find(
          (ap) => ap.id === attackPathId,
        );

        return {
          ...scenario,
          context: {
            sourceRisk: attackPath.sourceRiskName || "Not specified",
            targetObjective:
              attackPath.objectifVise ||
              attackPath.targetObjective ||
              "Not specified",
            dreadedEvent: attackPath.dreadedEventName || "Not specified",
            businessAssets: attackPath.businessAssets || [],
            ctiMetadata: ctiPath?.ctiMetadata,
            ctiCoverage: ctiPath?.ctiCoverage,
          },
        };
      });

      // Override scenario severity with dreaded event severity
      const enrichedScenarios = enrichScenariosWithDreadedEventSeverity(
        allScenarios,
        allAttackPaths,
        dreadedEvents,
      );

      // Per-scenario fresh PNGs — each scenario gets its own combined graph
      const scenariosToCapture = enrichedScenarios.filter(
        (s) => (s.modesOperatoires || []).length > 0,
      );
      if (scenariosToCapture.length > 0) {
        setCaptureScenarios(scenariosToCapture);
        // Wait for the hidden graphs to mount and fitView
        await new Promise((r) => setTimeout(r, 600));
        for (const s of scenariosToCapture) {
          const key = getScenarioKey(s);
          const png = reportCaptureRefs.current[key]
            ? await reportCaptureRefs.current[key]()
            : null;
          console.log('[EXPORT-PNG]', key, png ? { len: png.dataUrl?.length, w: png.width, h: png.height } : null);
          // ponytail: 10KB floor = blank-image guard; real graphs are 100KB+
          if (png && png.dataUrl && png.dataUrl.length > 10000 && png.width > 0 && png.height > 0) {
            s.vizPng = png.dataUrl;
            s.vizPngWidth = png.width;
            s.vizPngHeight = png.height;
          }
        }
        setCaptureScenarios([]);
      }

      // Import PDF generator dynamically (bilingual — English mode)
      const OperationalScenariosPdfGenerator = (
        await import("../../utils/operationalScenariosPdfGenerator")
      ).default;

      // Normalize scenarios and calculate statistics
      const normalizedScenarios = normalizeScenarios(enrichedScenarios);
      const { severityDistribution, likelihoodDistribution } =
        calculateDistributions(enrichedScenarios);

      // Count unique attack paths
      const uniqueAttackPathsEn = new Set(
        enrichedScenarios
          .map((s) => s.attackPathId || s.attackPathReference)
          .filter(Boolean),
      );

      const reportData = {
        scenarios: normalizedScenarios,
        summary: {
          totalScenarios: enrichedScenarios.length,
          totalAttackPaths: uniqueAttackPathsEn.size,
          criticalScenarios: severityDistribution.critical,
          highSeverityScenarios: severityDistribution.high,
        },
        severityDistribution,
        likelihoodDistribution,
      };

      const analysisData = {
        id: currentAnalysis?.id,
        name: currentAnalysis?.name,
        companyName: currentAnalysis?.companyName,
      };

      const pdfGenerator = new OperationalScenariosPdfGenerator(
        reportData,
        analysisData,
        null,
        "en",
      );
      const pdfBlob = await pdfGenerator.generateReport();

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `operational-scenarios-${currentAnalysis?.name || "analysis"}-${new Date().toISOString().split("T")[0]}-EN.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      showSuccessToast("PDF exported successfully");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      showErrorToast("Error exporting PDF");
    } finally {
      setIsExporting(false);
    }
  };

  // Export ALL scenarios to DOCX (French)
  const handleExportDocx = async () => {
    setIsExporting(true);
    try {
      // Reload ALL saved scenarios and get them directly (not from state which may be filtered)
      const allSavedScenarios = await loadSavedScenarios();

      const rawScenarios = [...generatedScenarios, ...allSavedScenarios];

      if (rawScenarios.length === 0) {
        showErrorToast("Aucun scénario à exporter");
        return;
      }

      const allScenarios = rawScenarios.map((scenario) => {
        const attackPath =
          allAttackPaths.find((ap) => ap.id === scenario.attackPathId) || {};
        const ctiPath = (ctiData?.attackPathsWithCTI || []).find(
          (ap) => ap.id === scenario.attackPathId,
        );
        return {
          ...scenario,
          context: {
            sourceRisk: attackPath.sourceRiskName || "Non spécifié",
            targetObjective:
              attackPath.objectifVise ||
              attackPath.targetObjective ||
              "Non spécifié",
            dreadedEvent: attackPath.dreadedEventName || "Non spécifié",
            businessAssets: attackPath.businessAssets || [],
            ctiMetadata: ctiPath?.ctiMetadata,
            ctiCoverage: ctiPath?.ctiCoverage,
          },
        };
      });

      const enrichedScenarios = enrichScenariosWithDreadedEventSeverity(
        allScenarios,
        allAttackPaths,
        dreadedEvents,
      );

      const generateDocx = (
        await import("../../utils/operationalScenariosDocxGenerator")
      ).default;

      // Normalize scenarios for consistent display
      const normalizedScenarios = normalizeScenarios(enrichedScenarios);

      const reportData = {
        scenarios: normalizedScenarios,
        attackPaths: allAttackPaths,
        analysisName: currentAnalysis?.name,
        organizationName: currentAnalysis?.companyName,
      };

      const analysisData = {
        id: currentAnalysis?.id,
        name: currentAnalysis?.name,
        companyName: currentAnalysis?.companyName,
      };

      await generateDocx(reportData, analysisData);
      showSuccessToast("DOCX exporté avec succès");
    } catch (error) {
      console.error("Erreur exportation DOCX:", error);
      showErrorToast("Erreur lors de l'exportation Word");
    } finally {
      setIsExporting(false);
    }
  };

  // Export ALL scenarios to DOCX (English)
  const handleExportDocxEn = async () => {
    setIsExporting(true);
    try {
      // Reload ALL saved scenarios and get them directly (not from state which may be filtered)
      const allSavedScenarios = await loadSavedScenarios();

      const rawScenarios = [...generatedScenarios, ...allSavedScenarios];

      if (rawScenarios.length === 0) {
        showErrorToast("No scenarios to export");
        return;
      }

      const allScenarios = rawScenarios.map((scenario) => {
        const attackPath =
          allAttackPaths.find((ap) => ap.id === scenario.attackPathId) || {};
        const ctiPath = (ctiData?.attackPathsWithCTI || []).find(
          (ap) => ap.id === scenario.attackPathId,
        );
        return {
          ...scenario,
          context: {
            sourceRisk: attackPath.sourceRiskName || "Not specified",
            targetObjective:
              attackPath.objectifVise ||
              attackPath.targetObjective ||
              "Not specified",
            dreadedEvent: attackPath.dreadedEventName || "Not specified",
            businessAssets: attackPath.businessAssets || [],
            ctiMetadata: ctiPath?.ctiMetadata,
            ctiCoverage: ctiPath?.ctiCoverage,
          },
        };
      });

      const enrichedScenarios = enrichScenariosWithDreadedEventSeverity(
        allScenarios,
        allAttackPaths,
        dreadedEvents,
      );

      const generateDocx = (
        await import("../../utils/operationalScenariosDocxGenerator")
      ).default;

      // Normalize scenarios for consistent display
      const normalizedScenarios = normalizeScenarios(enrichedScenarios);

      const reportData = {
        scenarios: normalizedScenarios,
        attackPaths: allAttackPaths,
        analysisName: currentAnalysis?.name,
        organizationName: currentAnalysis?.companyName,
      };

      const analysisData = {
        id: currentAnalysis?.id,
        name: currentAnalysis?.name,
        companyName: currentAnalysis?.companyName,
      };

      await generateDocx(reportData, analysisData, "en");
      showSuccessToast("DOCX exported successfully");
    } catch (error) {
      console.error("Error exporting DOCX:", error);
      showErrorToast("Error exporting Word");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoadingCTI) {
    return (
      <ErrorBoundary>
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <InlineLoader
              message="Chargement des données CTI et des chemins d'attaque..."
              size="lg"
              className="justify-center py-8"
            />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (!ctiData) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Aucun chemin d'attaque avec analyse CTI
        </h3>
        <p className="text-gray-600 mb-4">
          Effectuez d'abord une analyse CTI pour vos chemins d'attaque dans
          l'onglet "Intelligence des menaces"
        </p>
        <button
          onClick={loadAttackPathsAndCTI}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Recharger
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div ref={formChangeRef} className="space-y-6">
        {/* Skip Links for Accessibility */}
        <SkipLink href="#main-content">
          {t("workshop4.skipLinks.mainContent", "Skip to main content")}
        </SkipLink>
        <SkipLink href="#attack-path-selection">
          {t(
            "workshop4.skipLinks.attackPathSelection",
            "Skip to attack path selection",
          )}
        </SkipLink>
        <SkipLink href="#scenario-generation">
          {t(
            "workshop4.skipLinks.scenarioGeneration",
            "Skip to scenario generation",
          )}
        </SkipLink>

        {/* === HEADER === */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* Left Side: Breadcrumb & Title */}
            <div>
              <div className="flex items-center text-sm text-slate-500 mb-2">
                <span className="hover:text-blue-600 transition-colors">
                  {t("workshop4.header.breadcrumb", "Workshop 4")}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mx-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="font-medium text-blue-600">
                  {t(
                    "workshop4.operationalScenarios.title",
                    "Operational Scenarios",
                  )}
                </span>
              </div>
              <div className="flex items-center">
                <div className="bg-blue-100 p-2 rounded-lg mr-4">
                  <Brain size={24} className="text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">
                    {t(
                      "workshop4.operationalScenarios.title",
                      "Operational Scenarios",
                    )}
                  </h1>
                  <p className="text-sm text-slate-600 mt-1">
                    {t(
                      "workshop4.header.subtitle",
                      "Generate detailed operational scenarios using AI from cyber threat indicators and threat intelligence",
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Side: Export PDF Button */}
            <div className="flex items-center gap-3">
              <WorkshopExportMenu
                onExportPdfFr={handleExportPDF}
                onExportPdfEn={handleExportPDFEn}
                onExportDocxFr={handleExportDocx}
                onExportDocxEn={handleExportDocxEn}
                isExporting={isExporting}
              />

              <Button
                variant="primary"
                color="blue"
                icon={<Info />}
                onClick={() => setShowGuide(true)}
              >
                {t("workshop4.operationalScenarios.actions.help", "Aide")}
              </Button>
            </div>
          </div>
        </div>
        {/* === END HEADER === */}

        {/* Guide Modal - positioned right under the header */}
        <AnimatePresence>
          {showGuide && (
            <GuideModal
              isOpen={showGuide}
              onClose={() => setShowGuide(false)}
            />
          )}
        </AnimatePresence>

        {/* Error Display */}
        {currentError && (
          <ErrorDisplay
            error={currentError}
            onRetry={() => {
              setCurrentError(null);
              // Retry the last operation if possible
              if (selectedAssets.length > 0) {
                generateOperationalScenarios();
              }
            }}
            onDismiss={() => setCurrentError(null)}
            showDetails={showErrorDetails}
            showRecoveryActions={true}
            className="mb-6"
          />
        )}

        {/* Validation Errors Display */}
        {Object.keys(validationErrors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center mb-2">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
              <h3 className="text-sm font-semibold text-red-800">
                {t("workshop4.validation.errors", "Validation Errors")}
              </h3>
            </div>
            <div className="space-y-2">
              {Object.entries(validationErrors).map(([field, errors]) => (
                <div key={field}>
                  <div className="text-sm font-medium text-red-700 capitalize">
                    {field}:
                  </div>
                  <ul className="list-disc list-inside text-sm text-red-600 ml-4">
                    {errors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Validation Warnings removed - informational only */}

        {/* Generation Loading State */}
        {isGenerating && (
          <ScenarioGenerationLoader
            stage={generationStage}
            progress={generationProgress}
            message={generationMessage}
            className="mb-6"
          />
        )}

        {/* Enhanced Attack Path Selection */}
        <section
          id="attack-path-selection"
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          {...getAriaLabels.list("Section de sélection des chemins d'attaque")}
        >
          {/* Simplified Section Header */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Eye className="h-5 w-5 text-blue-700" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {t("workshop4.attackPathSection.title")}
                </h2>
              </div>

              {/* Data Summary */}
              {/* Data Summary (Decomposed) */}
              <div className="flex items-center space-x-4">
                {/* 1. CTI Progress Component */}
                <div className="bg-white rounded-xl px-4 py-2 border border-blue-200 shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="flex flex-col items-center">
                    <div className="flex items-baseline">
                      <span className="text-xl font-extrabold text-blue-700 leading-none">
                        {displayPaths.length}
                      </span>
                      <span className="mx-2 text-blue-200 font-light text-xl leading-none">
                        /
                      </span>
                      <span className="text-sm font-bold text-slate-400 leading-none">
                        {totalEligiblePaths}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                      {t("workshop4.ctiDisplay.ctiPaths")}
                    </div>
                  </div>
                </div>

                {/* 2. Missing Scenarios Component */}
                {pathsWithoutScenarios > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="bg-red-50 rounded-xl px-4 py-2 border border-red-100 shadow-sm hover:shadow-md transition-all duration-300 group"
                  >
                    <div className="flex flex-col items-center">
                      <div className="flex items-center text-red-600">
                        <AlertTriangle
                          size={16}
                          className="mr-2 group-hover:scale-110 transition-transform"
                        />
                        <span className="text-xl font-extrabold leading-none">
                          {pathsWithoutScenarios}
                        </span>
                      </div>
                      <div className="text-[10px] text-red-400 font-bold uppercase tracking-widest mt-1">
                        {t("workshop4.ctiDisplay.pathsWithoutScenarios")}
                      </div>
                    </div>
                  </motion.div>
                )}
                <button
                  onClick={loadAttackPathsAndCTI}
                  className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 border border-slate-200 hover:border-blue-300"
                  title="Actualiser les données"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {preliminaryHiddenCount > 0 && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-purple-50 border border-purple-200">
                <Info className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-purple-800">
                  {t('workshop4.preliminaryHiddenBanner', {
                    count: preliminaryHiddenCount,
                    defaultValue: `${preliminaryHiddenCount} chemin(s) d'attaque en mode étude préliminaire — non traités en Atelier 4.`,
                  })}
                </p>
              </div>
            )}
            {!ctiData.attackPathsWithCTI ||
            displayPaths.length === 0 ? (
              /* Enhanced Empty State */
              <div className="text-center py-12 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border-2 border-dashed border-slate-300">
                <div className="max-w-md mx-auto">
                  <div className="mb-6">
                    <div className="relative">
                      <Target className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                        <AlertTriangle className="h-3 w-3 text-red-500" />
                      </div>
                    </div>
                  </div>

                  <h4 className="text-xl font-semibold text-slate-900 mb-3">
                    Aucun chemin d'attaque avec analyse CTI
                  </h4>
                  <p className="text-slate-600 mb-6">
                    Pour générer des scénarios opérationnels avec l'IA, vous
                    devez d'abord analyser vos chemins d'attaque avec la CTI.
                  </p>

                  {/* Step-by-step Guide */}
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200 mb-6">
                    <h5 className="font-semibold text-slate-800 mb-3 text-left">
                      Étapes à suivre :
                    </h5>
                    <div className="space-y-3 text-left">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                          1
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">
                            Créer des chemins d'attaque
                          </div>
                          <div className="text-sm text-slate-600">
                            Dans l'Atelier 3, définissez vos chemins d'attaque
                            avec les actifs concernés
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                          2
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">
                            Effectuer l'analyse CTI
                          </div>
                          <div className="text-sm text-slate-600">
                            Dans l'onglet "Intelligence des menaces", analysez
                            vos chemins avec la CTI
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold">
                          3
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">
                            Générer les scénarios
                          </div>
                          <div className="text-sm text-slate-600">
                            Revenez ici pour sélectionner et générer vos
                            scénarios opérationnels
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-slate-100 rounded-lg p-3">
                      <div className="font-semibold text-slate-800">
                        {totalEligiblePaths}
                      </div>
                      <div className="text-slate-600">
                        Chemins d'attaque totaux
                      </div>
                    </div>
                    <div className="bg-slate-100 rounded-lg p-3">
                      <div className="font-semibold text-slate-800">0</div>
                      <div className="text-slate-600">Avec analyse CTI</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Enhanced Attack Path Cards */
              <div className="space-y-4">
                {/* Filter and Sort Controls */}
                <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-slate-700">
                      {t("workshop4.operationalScenarios.actions.attackPaths", {
                        count: displayPaths.length,
                      })}
                    </span>
                    {selectedAssets.length > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {selectedAssets.length}{" "}
                        {t("workshop4.operationalScenarios.table.selected")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {t("workshop4.attackPathCarousel.clickToSelect")}
                  </div>
                </div>

                {/* Attack Path Grid */}
                <div className="grid gap-4 lg:grid-cols-2">
                  {(() => {
                    const paths = displayPaths;
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const paginatedPaths = paths.slice(
                      startIndex,
                      startIndex + itemsPerPage,
                    );

                    return paginatedPaths.map((attackPath) => {
                      const isSelected = selectedAssets.includes(attackPath.id);
                      const hasVulnerabilities =
                        (attackPath.vulnerabilities?.length || 0) > 0;
                      const hasTechniques =
                        (attackPath.attackTechniques?.length || 0) > 0;

                      // Calculate status distribution (called per card, no hook inside loop)
                      const statusDist = calculateStatusDistribution(
                        attackPath.vulnerabilities,
                        attackPath.attackTechniques,
                        ctiItemStatuses,
                      );
                      const dataQuality =
                        hasVulnerabilities && hasTechniques
                          ? "excellent"
                          : hasVulnerabilities || hasTechniques
                            ? "good"
                            : "limited";

                      return (
                        <div
                          key={attackPath.id}
                          className={`
                            relative group cursor-pointer transition-all duration-300 rounded-lg border overflow-hidden
                            ${
                              isSelected
                                ? "border-blue-500 bg-blue-50 shadow-lg ring-1 ring-blue-200"
                                : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md"
                            }
                          `}
                          onClick={() => handleAssetToggle(attackPath.id)}
                        >
                          {/* Compact Header */}
                          <div
                            className={`px-4 py-3 ${isSelected ? "bg-blue-100" : "bg-slate-50"}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                {attackPath.referenceCode && (
                                  <div className="flex items-center gap-1.5">
                                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">
                                      {attackPath.referenceCode}
                                    </span>
                                    {(() => {
                                      // Try to find the reference from various possible sources
                                      let stratRef =
                                        attackPath.strategicScenarioReference ||
                                        attackPath.scenarioReference ||
                                        attackPath.strategicReference ||
                                        attackPath.strategicScenario
                                          ?.referenceCode;

                                      // If not found, look up in allAttackPaths
                                      if (
                                        !stratRef &&
                                        allAttackPaths.length > 0
                                      ) {
                                        const originalPath =
                                          allAttackPaths.find(
                                            (p) =>
                                              p.id === attackPath.id ||
                                              p._id === attackPath.id,
                                          );
                                        stratRef =
                                          originalPath?.strategicScenarioReference ||
                                          originalPath?.scenarioReference ||
                                          originalPath?.strategicReference ||
                                          originalPath?.strategicScenario
                                            ?.referenceCode;
                                      }

                                      // Final heuristic for CAD (Direct) paths: CAD01 -> S01
                                      if (
                                        !stratRef &&
                                        attackPath.referenceCode?.startsWith(
                                          "CAD",
                                        )
                                      ) {
                                        const num =
                                          attackPath.referenceCode.substring(3);
                                        if (num) stratRef = `S${num}`;
                                      }

                                      return stratRef ? (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 text-slate-700 border border-slate-300">
                                          {stratRef}
                                        </span>
                                      ) : null;
                                    })()}
                                  </div>
                                )}
                                <div
                                  className={`w-2 h-2 rounded-full ${
                                    dataQuality === "excellent"
                                      ? "bg-green-500"
                                      : dataQuality === "good"
                                        ? "bg-yellow-500"
                                        : "bg-red-500"
                                  }`}
                                  title={`${t("workshop4.ctiDisplay.quality")}: ${dataQuality}`}
                                ></div>
                                {isSelected && (
                                  <CheckCircle className="h-4 w-4 text-blue-600" />
                                )}
                              </div>

                              {/* Vraisemblance + Scenario Status */}
                              <div className="flex items-center">
                                {(() => {
                                    const info = scenarioStatus[attackPath.id];
                                    const hasScenario = info?.exists;
                                    const v = info?.vraisemblance;
                                    if (v == null) return null;
                                    const vLevel = v >= 4 ? 4 : v >= 3 ? 3 : v >= 2 ? 2 : 1;
                                    const vBadgeColors = { 1: 'bg-green-100 text-green-700 border-green-200', 2: 'bg-yellow-100 text-yellow-700 border-yellow-200', 3: 'bg-orange-100 text-orange-700 border-orange-200', 4: 'bg-red-100 text-red-700 border-red-200' };
                                    const vDotColors = { 1: 'bg-green-500', 2: 'bg-yellow-500', 3: 'bg-orange-500', 4: 'bg-red-500' };
                                    return hasScenario ? (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${vBadgeColors[vLevel]}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${vDotColors[vLevel]} mr-1.5 animate-pulse`} />
                                        {t(`workshop4.operationalScenarios.actions.vraisemblanceLevels.${v >= 4 ? 'critical' : v >= 3 ? 'high' : v >= 2 ? 'medium' : 'low'}`)}
                                      </span>
                                    ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mr-1.5" />
                                      {t("workshop4.operationalScenarios.actions.noScenario", "Sans scénario")}
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                            <h4
                              className={`font-semibold mt-1 text-sm ${isSelected ? "text-blue-900" : "text-slate-900"}`}
                            >
                              {attackPath.pathName}
                            </h4>
                            {(() => {
                              const covInfo = getCtiCoverageInfo(attackPath);
                              if (!covInfo.isPartial) return null;
                              const badgeLabel = covInfo.analyzed != null && covInfo.total
                                ? `${covInfo.analyzed}/${covInfo.total} ${t("workshop4.ctiDisplay.assets", { defaultValue: "actifs" })}`
                                : t('workshop4.ctiDisplay.partialCoverage', { defaultValue: 'Couverture partielle' });
                              return (
                                <div className="mt-1.5 space-y-1.5">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    {badgeLabel}
                                  </span>
                                  {covInfo.uncoveredNames.length > 0 && (
                                    <div className="flex items-start gap-1.5 p-2 rounded-lg bg-amber-50 border border-amber-200">
                                      <Info className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                                      <p className="text-[11px] text-amber-800 leading-snug">
                                        {t('workshop4.ctiDisplay.uncoveredAssets', { defaultValue: 'CTI non couverte : ' })}
                                        {covInfo.uncoveredNames.join(", ")}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>

                          {/* Compact Content */}
                          <div className="px-4 py-3 space-y-3">
                            {/* Objective & Context Row */}
                            <div className="grid grid-cols-1 gap-2">
                              <div className="flex items-center space-x-2 text-xs">
                                <Target className="h-3 w-3 text-blue-600 flex-shrink-0" />
                                <span className="text-slate-600 font-medium">
                                  {t("workshop4.attackPathCarousel.objective")}
                                </span>
                                <span className="text-slate-900 truncate">
                                  {(attackPath.objectifVise &&
                                    attackPath.objectifVise.trim()) ||
                                    (attackPath.pathName &&
                                      attackPath.pathName.trim()) ||
                                    t(
                                      "workshop4.attackPathCarousel.noAttackPaths",
                                    )}
                                </span>
                              </div>
                            </div>

                            {/* Assets Summary */}
                            {(attackPath.businessAssets?.length > 0 ||
                              attackPath.supportAssets?.length > 0) && (
                              <div className="space-y-1">
                                {attackPath.businessAssets?.length > 0 && (
                                  <div className="flex items-center space-x-2 text-xs">
                                    <Shield className="h-3 w-3 text-green-600 flex-shrink-0" />
                                    <span className="text-green-700 font-medium">
                                      {t("navigation.businessValues")}:
                                    </span>
                                    <span className="text-slate-900 truncate">
                                      {attackPath.businessAssets
                                        .map((asset) => asset.name || asset)
                                        .slice(0, 2)
                                        .join(", ")}
                                      {attackPath.businessAssets.length > 2 &&
                                        ` (+${attackPath.businessAssets.length - 2})`}
                                    </span>
                                  </div>
                                )}
                                {attackPath.supportAssets?.length > 0 && (
                                  <div className="flex items-center space-x-2 text-xs">
                                    <Package className="h-3 w-3 text-purple-600 flex-shrink-0" />
                                    <span className="text-purple-700 font-medium">
                                      {t(
                                        "businessValues.sections.supportAssets",
                                      )}
                                      :
                                    </span>
                                    <span className="text-slate-900 font-bold">
                                      {attackPath.supportAssets.length}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* CTI Data Summary - Status Based (Premium Redesign) */}
                            <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100 shadow-sm">
                              <div className="grid grid-cols-5 gap-2 text-center">
                                {/* Assets */}
                                <div className="flex flex-col items-center p-1.5 rounded-lg transition-colors hover:bg-white group/stat">
                                  <Package
                                    size={16}
                                    strokeWidth={2.5}
                                    className="text-slate-400 mb-1 group-hover/stat:text-blue-500 transition-colors"
                                  />
                                  <div className="text-sm font-bold text-slate-900 leading-none mb-1">
                                    {attackPath.ctiMetadata?.totalAssets || 0}
                                  </div>
                                  <div className="text-[9px] text-slate-400 uppercase font-bold tracking-tight">
                                    {t("workshop4.ctiDisplay.assets")}
                                  </div>
                                </div>

                                {/* Not Fixed */}
                                <div
                                  title={t("workshop4.ctiDisplay.notFixed")}
                                  className="flex flex-col items-center p-1.5 rounded-lg transition-colors hover:bg-white group/stat"
                                >
                                  <AlertCircle
                                    size={16}
                                    strokeWidth={2.5}
                                    className={`${statusDist.non_corrige > 0 ? "text-red-500" : "text-slate-300"} mb-1`}
                                  />
                                  <div
                                    className={`text-sm font-bold leading-none mb-1 ${statusDist.non_corrige > 0 ? "text-red-600" : "text-slate-400"}`}
                                  >
                                    {statusDist.non_corrige}
                                  </div>
                                  <div className="text-[9px] text-slate-400 uppercase font-bold tracking-tight">
                                    Non corr.
                                  </div>
                                </div>

                                {/* Fixed */}
                                <div
                                  title={t("workshop4.ctiDisplay.fixed")}
                                  className="flex flex-col items-center p-1.5 rounded-lg transition-colors hover:bg-white group/stat"
                                >
                                  <CheckCircle2
                                    size={16}
                                    strokeWidth={2.5}
                                    className={`${statusDist.corrige > 0 ? "text-green-500" : "text-slate-300"} mb-1`}
                                  />
                                  <div
                                    className={`text-sm font-bold leading-none mb-1 ${statusDist.corrige > 0 ? "text-green-600" : "text-slate-400"}`}
                                  >
                                    {statusDist.corrige}
                                  </div>
                                  <div className="text-[9px] text-slate-400 uppercase font-bold tracking-tight">
                                    Corr.
                                  </div>
                                </div>

                                {/* Accepted */}
                                <div
                                  title={t("workshop4.ctiDisplay.riskAccepted")}
                                  className="flex flex-col items-center p-1.5 rounded-lg transition-colors hover:bg-white group/stat"
                                >
                                  <Shield
                                    size={16}
                                    strokeWidth={2.5}
                                    className={`${statusDist.risque_accepte > 0 ? "text-amber-500" : "text-slate-300"} mb-1`}
                                  />
                                  <div
                                    className={`text-sm font-bold leading-none mb-1 ${statusDist.risque_accepte > 0 ? "text-amber-500" : "text-slate-400"}`}
                                  >
                                    {statusDist.risque_accepte}
                                  </div>
                                  <div className="text-[9px] text-slate-400 uppercase font-bold tracking-tight">
                                    Accepté
                                  </div>
                                </div>

                                {/* Not Relevant */}
                                <div
                                  title={t("workshop4.ctiDisplay.notRelevant")}
                                  className="flex flex-col items-center p-1.5 rounded-lg transition-colors hover:bg-white group/stat"
                                >
                                  <MinusCircle
                                    size={16}
                                    strokeWidth={2.5}
                                    className={`${statusDist.non_pertinent > 0 ? "text-slate-500" : "text-slate-300"} mb-1`}
                                  />
                                  <div
                                    className={`text-sm font-bold leading-none mb-1 ${statusDist.non_pertinent > 0 ? "text-slate-600" : "text-slate-400"}`}
                                  >
                                    {statusDist.non_pertinent}
                                  </div>
                                  <div className="text-[9px] text-slate-400 uppercase font-bold tracking-tight">
                                    Non pert.
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Analysis Date */}
                            {attackPath.ctiMetadata?.analysisDate && (
                              <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500 text-center">
                                {t("workshop4.ctiDisplay.analyzedOn")}{" "}
                                {new Date(
                                  attackPath.ctiMetadata.analysisDate,
                                ).toLocaleDateString("fr-FR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "2-digit",
                                })}
                              </div>
                            )}

                            {/* Vulnerability & Technique Preview */}
                            {(hasVulnerabilities || hasTechniques) && (
                              <div className="p-2 space-y-1 bg-slate-50 rounded-lg border border-slate-100">
                                {hasVulnerabilities && (
                                  <div className="text-[10px] flex items-center">
                                    <span className="text-red-600 font-bold w-12 shrink-0">
                                      CVE:
                                    </span>
                                    <span className="text-slate-600 truncate">
                                      {attackPath.vulnerabilities
                                        .slice(0, 3)
                                        .map((vuln) => vuln.cveId || vuln.id)
                                        .join(", ")}
                                      {attackPath.vulnerabilities.length > 3 &&
                                        ` (+${attackPath.vulnerabilities.length - 3})`}
                                    </span>
                                  </div>
                                )}
                                {hasTechniques && (
                                  <div className="text-[10px] flex items-center">
                                    <span className="text-purple-600 font-bold w-12 shrink-0">
                                      MITRE:
                                    </span>
                                    <span className="text-slate-600 truncate">
                                      {attackPath.attackTechniques
                                        .slice(0, 3)
                                        .map(
                                          (tech) => tech.techniqueId || tech.id,
                                        )
                                        .join(", ")}
                                      {attackPath.attackTechniques.length > 3 &&
                                        ` (+${attackPath.attackTechniques.length - 3})`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Analyst Note */}
                            <textarea
                              rows={2}
                              placeholder={t(
                                "workshop4.ctiDisplay.notePlaceholder",
                                "Note d'analyse...",
                              )}
                              value={pathNotes[attackPath.id] || ""}
                              onChange={(e) =>
                                setPathNotes((prev) => ({
                                  ...prev,
                                  [attackPath.id]: e.target.value,
                                }))
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="w-full text-xs p-2 rounded-lg border border-slate-200 bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-200 resize-y"
                            />
                            {(() => {
                              const dirty =
                                (pathNotes[attackPath.id] || "") !==
                                (savedNotes[attackPath.id] || "");
                              return (
                                <div
                                  className="flex justify-end mt-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    disabled={
                                      !dirty ||
                                      savingPathId === attackPath.id
                                    }
                                    onClick={() => handleSaveNote(attackPath.id)}
                                    className={`text-[10px] px-2 py-1 rounded ${
                                      dirty
                                        ? "bg-blue-600 text-white hover:bg-blue-700"
                                        : "bg-slate-100 text-slate-400 cursor-not-allowed"
                                    }`}
                                  >
                                    {savingPathId === attackPath.id
                                      ? "..."
                                      : t("common.save", "Enregistrer")}
                                  </button>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Selection Overlay */}
                          {isSelected && (
                            <div className="absolute inset-0 bg-blue-500 bg-opacity-5 pointer-events-none">
                              <div className="absolute top-3 right-3">
                                <div className="bg-blue-500 text-white rounded-full p-1 shadow-lg">
                                  <CheckCircle className="h-4 w-4" />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Pagination Controls */}
                {displayPaths.length > itemsPerPage && (
                  <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6 mt-4">
                    <div className="flex flex-1 justify-between sm:hidden">
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        Précédent
                      </button>
                      <button
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(
                              prev + 1,
                              Math.ceil(
                                displayPaths.length /
                                  itemsPerPage,
                              ),
                            ),
                          )
                        }
                        disabled={
                          currentPage ===
                          Math.ceil(
                            displayPaths.length / itemsPerPage,
                          )
                        }
                        className={`relative ml-3 inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 ${currentPage === Math.ceil(displayPaths.length / itemsPerPage) ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        Suivant
                      </button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-slate-700">
                          Affichage de{" "}
                          <span className="font-medium">
                            {(currentPage - 1) * itemsPerPage + 1}
                          </span>{" "}
                          à{" "}
                          <span className="font-medium">
                            {Math.min(
                              currentPage * itemsPerPage,
                              displayPaths.length,
                            )}
                          </span>{" "}
                          sur{" "}
                          <span className="font-medium">
                            {displayPaths.length}
                          </span>{" "}
                          chemins d'attaque
                        </p>
                      </div>
                      <div>
                        <nav
                          className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                          aria-label="Pagination"
                        >
                          <button
                            onClick={() =>
                              setCurrentPage((prev) => Math.max(prev - 1, 1))
                            }
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:z-20 focus:outline-offset-0 ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            <span className="sr-only">Précédent</span>
                            <ArrowRight
                              className="h-5 w-5 rotate-180"
                              aria-hidden="true"
                            />
                          </button>

                          {/* Page Numbers */}
                          {Array.from({
                            length: Math.ceil(
                              displayPaths.length / itemsPerPage,
                            ),
                          }).map((_, idx) => (
                            <button
                              key={idx + 1}
                              onClick={() => setCurrentPage(idx + 1)}
                              aria-current={
                                currentPage === idx + 1 ? "page" : undefined
                              }
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                currentPage === idx + 1
                                  ? "z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                  : "text-slate-900 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:z-20 focus:outline-offset-0"
                              }`}
                            >
                              {idx + 1}
                            </button>
                          ))}

                          <button
                            onClick={() =>
                              setCurrentPage((prev) =>
                                Math.min(
                                  prev + 1,
                                  Math.ceil(
                                    displayPaths.length /
                                      itemsPerPage,
                                  ),
                                ),
                              )
                            }
                            disabled={
                              currentPage ===
                              Math.ceil(
                                displayPaths.length /
                                  itemsPerPage,
                              )
                            }
                            className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:z-20 focus:outline-offset-0 ${currentPage === Math.ceil(displayPaths.length / itemsPerPage) ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            <span className="sr-only">Suivant</span>
                            <ArrowRight
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Generation Controls */}
        <section
          id="scenario-generation"
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          {...getAriaLabels.list("Section de génération de scénarios")}
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900">
              {t("workshop4.generation.title")}
            </h2>

            <div className="flex flex-wrap gap-4 items-center justify-start">
              {(() => {
                const pathId = selectedAssets[0];
                const hasScenario = pathId && (
                  generatedScenarios.some((s) => s.attackPathId === pathId) ||
                  existingScenarios.some((s) => s.attackPathId === pathId)
                );

                if (!pathId || hasScenario) return null;

                return (
                  <>
                    {/* Manual Scenario Button */}
                    <button
                      onClick={handleAddManualScenario}
                      className="
                        group relative inline-flex items-center px-5 py-3 rounded-xl font-medium text-sm
                        bg-gradient-to-r from-gray-600 to-gray-700 text-white shadow-lg
                        hover:shadow-xl hover:from-gray-700 hover:to-gray-800 
                        transform hover:scale-105 transition-all duration-300
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500
                      "
                    >
                      <Plus className="h-4 w-4 mr-3 group-hover:rotate-180 transition-transform duration-300" />
                      <span>{t("workshop4.generation.buttons.manualScenario")}</span>
                    </button>

                    {/* AI Generation Button */}
                    {companyAiEnabled && (
                      <button
                        onClick={generateOperationalScenarios}
                        disabled={isGenerating}
                        className={`
                          p-3 rounded-xl transition-all duration-200 shadow-sm
                          focus:outline-none focus:ring-2 focus:ring-offset-1
                          ${
                            isGenerating
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500"
                          }
                        `}
                        title={
                          isGenerating
                            ? "Génération en cours..."
                            : "Générer des scénarios avec l'IA"
                        }
                        aria-label={
                          isGenerating
                            ? "Génération de scénarios en cours"
                            : "Générer les scénarios opérationnels"
                        }
                      >
                        {isGenerating ? (
                          <RefreshCw size={20} className="animate-spin" />
                        ) : (
                          <BrainCircuit size={20} />
                        )}
                      </button>
                    )}
                  </>
                );
              })()}

              {/* Show placeholder when no attack path is selected */}
              {companyAiEnabled && selectedAssets.length === 0 && (
                <button
                  disabled={true}
                  className="p-3 rounded-xl bg-gray-100 text-gray-400 cursor-not-allowed shadow-sm"
                  title="Sélectionnez un chemin d'attaque"
                >
                  <BrainCircuit size={20} />
                </button>
              )}

              {(() => {
                const selectedAttackPathId = selectedAssets[0];
                const filteredGeneratedScenarios = generatedScenarios.filter(
                  (scenario) => scenario.attackPathId === selectedAttackPathId,
                );
                return filteredGeneratedScenarios.length > 0;
              })() && (
                <button
                  onClick={saveScenarios}
                  className="
                      group relative inline-flex items-center px-5 py-3 rounded-xl font-medium text-sm
                      bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg
                      hover:shadow-xl hover:from-green-700 hover:to-emerald-700 
                      transform hover:scale-105 transition-all duration-300
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
                    "
                >
                  <Save className="h-4 w-4 mr-3 group-hover:animate-bounce" />
                  <span>Sauvegarder</span>
                </button>
              )}

              {/* Selection Help Text */}
              {ctiData?.allAttackPaths &&
                ctiData.allAttackPaths.length > 0 &&
                selectedAssets.length === 0 && (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center space-x-3 text-gray-600 bg-blue-50 rounded-full px-6 py-3 border border-blue-200">
                      <Target className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium">
                        Cliquez sur un chemin d'attaque pour le sélectionner et
                        générer des scénarios
                      </span>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </section>

        {/* Scenario Edit Modal with Vraisemblance Support */}
        <ScenarioEditModal
          scenario={editingScenario}
          isOpen={!!editingScenario}
          onClose={cancelEdit}
          onSave={saveEditedScenario}
          isLoading={false}
          ctiData={(() => {
            // Get vulnerabilities specific to this scenario's attack path
            const attackPathId = editingScenario?.attackPathId;
            const attackPathWithCTI = ctiData?.attackPathsWithCTI?.find(
              (ap) => ap.id === attackPathId,
            );
            console.log(
              "[ScenarioEditModal props] attackPathWithCTI:",
              attackPathWithCTI,
            );
            console.log(
              "[ScenarioEditModal props] vulnerabilities:",
              attackPathWithCTI?.vulnerabilities,
            );
            return {
              // Vulnerabilities are directly on the attack path, not nested in ctiResults
              vulnerabilities: attackPathWithCTI?.vulnerabilities || [],
            };
          })()}
          initialTab={editModalTab}
          businessAssets={(() => {
            // Get support assets from the editing scenario's attack path
            const attackPathId = editingScenario?.attackPathId;
            const attackPath = allAttackPaths.find(
              (ap) => ap.id === attackPathId,
            );
            // Also check attackPathsWithCTI for more complete data
            const attackPathWithCTI = ctiData?.attackPathsWithCTI?.find(
              (ap) => ap.id === attackPathId,
            );

            // Try multiple possible locations for assets
            const assets =
              attackPath?.supportAssets ||
              attackPath?.businessAssets ||
              attackPathWithCTI?.supportAssets ||
              attackPathWithCTI?.businessAssets ||
              attackPathWithCTI?.analyzedAssets ||
              [];

            console.log("[ScenarioEditModal props] attackPath:", attackPath);
            console.log("[ScenarioEditModal props] assets found:", assets);
            return assets;
          })()}
        />

        {/* Scenario Management Display */}
        {selectedAssets.length > 0 &&
          (() => {
            const selectedAttackPathId = selectedAssets[0];
            const selectedAttackPath = allAttackPaths.find(
              (ap) => ap.id === selectedAttackPathId,
            );

            const filteredGeneratedScenarios = generatedScenarios.filter(
              (scenario) => scenario.attackPathId === selectedAttackPathId,
            );

            const filteredExistingScenarios = existingScenarios.filter(
              (scenario) => scenario.attackPathId === selectedAttackPathId,
            );

            return (
              filteredGeneratedScenarios.length > 0 ||
              filteredExistingScenarios.length > 0
            );
          })() && (
            <>
              <ScenarioManager
                scenarios={(() => {
                  const selectedAttackPathId = selectedAssets[0];
                  return generatedScenarios.filter(
                    (scenario) =>
                      scenario.attackPathId === selectedAttackPathId,
                  );
                })()}
                existingScenarios={(() => {
                  const selectedAttackPathId = selectedAssets[0];
                  const selectedAttackPath = allAttackPaths.find(
                    (ap) => ap.id === selectedAttackPathId,
                  );
                  return existingScenarios.filter(
                    (scenario) =>
                      scenario.attackPathId === selectedAttackPathId,
                  );
                })()}
                scenarioStats={scenarioStats}
                selectedAttackPath={(
                  filteredCtiData?.attackPathsWithCTI ||
                  ctiData?.attackPathsWithCTI
                )?.find((path) => selectedAssets.includes(path.id))}
                analysisId={currentAnalysis?.id}
                onEditScenario={handleEditScenario}
                onAddMode={handleAddMode}
                onDeleteScenario={(scenario) => {
                  // Show confirmation modal instead of window.confirm
                  setScenarioToDelete(scenario);
                  setShowDeleteConfirmation(true);
                }}
                onPhaseVisualization={handlePhaseVisualization}
                onDeleteMode={handleDeleteMode}
                onToggleRetained={handleToggleRetained}
                onSaveCritique={handleSaveCritique}
                onScenarioUpdate={async (updatedScenario) => {
                   try {
                       const scenarioId = getScenarioKey(updatedScenario);
                       const enhanced = ensureScenarioId(updatedScenario);

                       console.log('[DEBUG-onScenarioUpdate] steps in updatedScenario:', (updatedScenario.operationalSteps || []).length, 'first 3:', (updatedScenario.operationalSteps || []).slice(0, 3).map(s => s.elementaryActionId));
                       console.log('[DEBUG-onScenarioUpdate] enhanced id:', scenarioId);
                       console.log('[DEBUG-onScenarioUpdate] existingScenarios count:', existingScenarios.length, 'has match:', existingScenarios.some(s => getScenarioKey(s) === scenarioId));
                       console.log('[DEBUG-onScenarioUpdate] generatedScenarios count:', generatedScenarios.length, 'has match:', generatedScenarios.some(s => getScenarioKey(s) === scenarioId));

                       // Update local state immediately
                      setExistingScenarios((prev) =>
                        prev.map((s) => getScenarioKey(s) === scenarioId ? enhanced : s),
                      );
                      setGeneratedScenarios((prev) =>
                        prev.map((s) => getScenarioKey(s) === scenarioId ? enhanced : s),
                      );

                      // Build combined list from existing + generated scenarios
                      const allCurrentScenarios = [...existingScenarios, ...generatedScenarios]
                         .map((s) =>
                           getScenarioKey(s) === scenarioId ? enhanced : s
                         )
                        .map(ensureScenarioId);

                     // Deduplicate by getScenarioKey
                      const uniqueScenarios = allCurrentScenarios.reduce(
                        (acc, scenario) => {
                          const key = getScenarioKey(scenario);
                          if (!acc.find((s) => getScenarioKey(s) === key)) {
                            acc.push(scenario);
                          }
                          return acc;
                        },
                        [],
                      );

                      console.log('[DEBUG-onScenarioUpdate] uniqueScenarios count:', uniqueScenarios.length);
                      console.log('[DEBUG-onScenarioUpdate] uniqueScenarios[0] steps:', (uniqueScenarios[0]?.operationalSteps || []).length, 'first 3:', (uniqueScenarios[0]?.operationalSteps || []).slice(0, 3).map(s => s.elementaryActionId));
                      console.log('[DEBUG-onScenarioUpdate] uniqueScenarios[0] modesOperatoires:', (uniqueScenarios[0]?.modesOperatoires || []).length);

                      await saveCTIScenarios(
                       currentAnalysis.id,
                       uniqueScenarios,
                       null,
                      );

                      setScenarioStatus(prev => ({
                        ...prev,
                        [updatedScenario.attackPathId]: {
                          exists: true,
                          vraisemblance: updatedScenario.riskLevel ?? updatedScenario.scenarioVraisemblance ?? updatedScenario.likelihood ?? null,
                        }
                      }));

                      showSuccessToast(
                       "Paramètres de risque sauvegardés avec succès",
                     );
                   } catch (error) {
                     console.error(
                       "❌ [OperationalScenariosAI] Error updating scenario:",
                       error,
                     );
                     showErrorToast(
                       "Erreur lors de la sauvegarde des paramètres de risque",
                     );
                   }
                  }}
                 onVraisemblancePreview={(attackPathId, v) => {
                    console.log('[BADGE] preview attackPath:', attackPathId, '| v:', v);
                    setScenarioStatus(prev => ({
                      ...prev,
                      [attackPathId]: { exists: true, vraisemblance: v },
                    }));
                  }}
                 isLoading={isLoadingExistingScenarios}
                 onCaptureReady={(fn) => { inlineVizCaptureRef.current = fn; }}
               />
            </>
          )}

        {/* Hidden per-scenario capture graphs for report export */}
        {captureScenarios.length > 0 && (
          <div
            style={{ position: 'fixed', left: -9999, top: 0, width: 1100, pointerEvents: 'none' }}
            aria-hidden
          >
            {captureScenarios.map((s) => {
              const key = getScenarioKey(s);
              return (
                <div key={key} style={{ width: 1100, height: 600 }}>
                  <ModesOperatoiresFlowVisualization
                    modesOperatoires={s.modesOperatoires || []}
                    operationalSteps={(() => {
                      const deduped = new Map();
                      [...(s.elementaryActions || []), ...(s.operationalSteps || s.steps || [])]
                        .forEach((st, idx) => deduped.set(String(st.elementaryActionId || st.id || st._id || idx), st));
                      return [...deduped.values()];
                    })()}
                    isInline={true}
                    onCaptureReady={(fn) => { reportCaptureRefs.current[key] = fn; }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Inline Phase Visualization (FR-024) */}
        {showPhaseVisualization && selectedPhaseScenario && (
          <section
            id="phase-visualization-section"
            className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 scroll-mt-20"
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {t("workshop4.risk.attackPhases")}: {selectedPhaseScenario.name}
                {selectedPhase && ` - Phase ${selectedPhase}`}
              </h3>
              <button
                onClick={closePhaseVisualization}
                className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X size={16} className="mr-1" />
                {t("common.close")}
              </button>
            </div>
            <div className="h-96" style={{ height: "600px" }}>
              <ReactFlowProvider>
                <PhaseVisualization
                  key={`phase-viz-${selectedPhaseScenario._id || selectedPhaseScenario.id}-${selectedPhase || "all"}`}
                  scenario={selectedPhaseScenario}
                  selectedPhase={selectedPhase}
                  onPhaseChange={handlePhaseChange}
                  onClose={closePhaseVisualization}
                  isModal={false}
                  isInline={true}
                />
              </ReactFlowProvider>
            </div>
          </section>
        )}

        {/* Phase Detail Modal (FR-024) */}
        {showPhaseDetailModal &&
          phaseDetailData.scenario &&
          phaseDetailData.phase && (
            <PhaseDetailModal
              scenario={phaseDetailData.scenario}
              phase={phaseDetailData.phase}
              onClose={closePhaseDetailModal}
              onVisualize={handlePhaseVisualization}
            />
          )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteConfirmation}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          scenario={scenarioToDelete}
        />

        {/* Enhanced Loading Overlay for AI Generation */}
        {isGenerating && (
          <EnhancedLoadingOverlay
            stage={generationStage}
            isComplete={isGenerationComplete}
            onComplete={() => {
              setIsGenerating(false);
              setIsGenerationComplete(false);
              setGenerationStage("preparing");
            }}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default OperationalScenariosAI;
